import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MapPin, Clock, Plus, Pencil, Check, X,
  Loader2, Eye, EyeOff, Send, Calendar, MessageSquare,
  BanIcon, ImageIcon, TrendingUp, Wallet, Banknote, AlertCircle, Sparkles, User, Crop, Bell
} from 'lucide-react';
import VideoUpload from '../components/VideoUpload';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { uploadFile } from '../lib/upload';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import PhotoCropper from '../components/PhotoCropper';
import DatePicker, { dateLocale } from '../components/DatePicker';
import { useConfig } from '../config';
import { INCLUDED_KEYS, NOT_INCLUDED_KEYS, itemLabel, normalizeItems } from '../lib/activityItems';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { isPushSupported, isIos, isStandalone, subscribeToPush, getPushSubscription } from '../lib/push';

var API_URL = process.env.REACT_APP_BACKEND_URL;

// Rend lisible une erreur API (y compris les erreurs de validation 422 = tableau).
function apiErr(err, fallback) {
  var d = err && err.response && err.response.data && err.response.data.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(function(x) { return x && x.msg ? x.msg : ''; }).filter(Boolean).join(' · ') || fallback;
  return fallback;
}

// Barre de chargement "ligne de métro de Tokyo" : une rame qui avance sur un
// rail jalonné de stations, le trajet se colore au passage. Indéterminé (boucle).
function TokyoRailLoader(props) {
  var stations = [8, 30, 52, 74, 94];
  return (
    <div className="mt-3" data-testid="tokyo-rail-loader">
      <div className="relative h-6">
        <div className="absolute top-1/2 left-0 right-0 h-1.5 -translate-y-1/2 bg-white/10 rounded-full" />
        <div className="absolute top-1/2 left-0 h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#F9004D] to-[#00F0FF]"
          style={{ animation: 'gmRailFill 2.4s cubic-bezier(0.65,0,0.35,1) infinite' }} />
        {stations.map(function(p, i) {
          return <span key={i} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-zinc-900 border-2 border-white/40"
            style={{ left: p + '%' }} />;
        })}
        <span className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-4 rounded-md bg-white shadow-[0_0_10px_rgba(255,255,255,0.6)] flex items-center justify-center"
          style={{ animation: 'gmRailTrain 2.4s cubic-bezier(0.65,0,0.35,1) infinite' }}>
          <span className="w-1 h-1 rounded-full bg-[#F9004D]" />
        </span>
      </div>
      <p className="text-zinc-400 text-xs mt-1 text-center">{props.label}</p>
      <style>{'@keyframes gmRailFill{0%{width:0}55%{width:96%}100%{width:96%}}@keyframes gmRailTrain{0%{left:0}55%{left:94%}100%{left:94%}}'}</style>
    </div>
  );
}

// Prix forfait le plus bas d'un séjour (0 si aucune grille renseignée).
function sejourMinPrice(activity) {
  var pkg = (activity && activity.package_prices) || {};
  var vals = Object.keys(pkg).map(function(k) { return parseInt(pkg[k], 10) || 0; }).filter(function(v) { return v > 0; });
  return vals.length ? Math.min.apply(null, vals) : 0;
}

function formatYen(amount) {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    minimumFractionDigits: 0
  }).format(amount || 0);
}

var STATUS_COLORS = {
  draft: 'bg-zinc-500/20 text-zinc-400',
  pending_review: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  inactive: 'bg-zinc-700/20 text-zinc-500'
};

var DAY_VALUES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Créneau (matin/après-midi/soir) dérivé de l'heure de début — conservé pour
// le filtre public et l'affichage, mais le guide choisit désormais son heure exacte.
function slotFromTime(hhmm) {
  var h = parseInt((hhmm || '09:00').split(':')[0], 10);
  if (isNaN(h)) return [];
  if (h < 12) return ['morning'];
  if (h < 18) return ['afternoon'];
  return ['evening'];
}

var POPULAR_LOCATIONS = [
  'Tokyo - Shibuya', 'Tokyo - Asakusa', 'Tokyo - Akihabara', 'Tokyo - Shinjuku',
  'Tokyo - Harajuku', 'Tokyo - Ginza', 'Tokyo - Roppongi', 'Tokyo - Ueno',
  'Kamakura', 'Osaka', 'Kyoto', 'Nara', 'Hiroshima', 'Hakone', 'Nikko', 'Yokohama', 'Kobe', 'Fukuoka'
];

// Les suggestions « inclus / non inclus » sont des CLÉS stables (pas du texte
// français) : chaque visiteur voit le libellé dans sa langue via i18n.
var INCLUDED_SUGGESTIONS = INCLUDED_KEYS;
var NOT_INCLUDED_SUGGESTIONS = NOT_INCLUDED_KEYS;

export function ActivityForm({ activity, onSave, onCancel }) {
  var { t } = useTranslation();
  useCurrency(); // re-render quand la devise d'affichage change (équivalents convertis)
  var isEdit = !!activity;
  var [form, setForm] = React.useState({
    location: activity ? activity.location : '',
    meeting_point: activity ? (activity.meeting_point || '') : '',
    meeting_point_url: activity ? (activity.meeting_point_url || '') : '',
    included: activity && activity.included ? normalizeItems(activity.included) : [],
    not_included: activity && activity.not_included ? normalizeItems(activity.not_included) : [],
    included_custom: '',
    not_included_custom: '',
    start_time: activity ? (activity.start_time || '09:00') : '09:00',
    duration_hours: activity ? (activity.duration_hours || 3) : 3,
    name_fr: activity ? (activity.name.fr || '') : '',
    name_en: activity ? (activity.name.en || '') : '',
    name_jp: activity ? (activity.name.jp || '') : '',
    description_fr: activity ? (activity.description.fr || '') : '',
    description_en: activity ? (activity.description.en || '') : '',
    description_jp: activity ? (activity.description.jp || '') : '',
    adult_rate: activity ? activity.adult_rate : 9000,
    child_rate: activity ? activity.child_rate : 5000,
    children_allowed: activity ? activity.children_allowed : true,
    max_participants: activity ? activity.max_participants : 6,
    min_notice_days: activity ? (activity.min_notice_days || 0) : 0,
    group_discount_enabled: activity ? !!activity.group_discount_enabled : false,
    group_discounts: activity ? (activity.group_discounts || {}) : {},
    days_of_week: activity && activity.availability ? (activity.availability.days_of_week || []) : [],
    time_slots: activity && activity.availability ? (activity.availability.time_slots || []) : [],
    start_date: activity && activity.availability && activity.availability.start_date ? activity.availability.start_date : '',
    end_date: activity && activity.availability && activity.availability.end_date ? activity.availability.end_date : '',
    // --- Séjour multi-jours ---
    activity_type: activity && activity.activity_type ? activity.activity_type : 'journee',
    destinations: activity && activity.destinations ? activity.destinations : [],
    destination_input: '',
    ai_instructions: '',
    duration_days: activity && activity.duration_days ? activity.duration_days : 3,
    itinerary: activity && activity.itinerary ? activity.itinerary : [],
    pricing_mode: activity && activity.pricing_mode ? activity.pricing_mode : 'forfait',
    min_participants: activity && activity.min_participants ? activity.min_participants : 2,
    package_prices: activity && activity.package_prices ? activity.package_prices : {}
  });
  var [saving, setSaving] = React.useState(false);
  var [photos, setPhotos] = React.useState(activity && activity.photos ? activity.photos : []);
  var [uploadingPhoto, setUploadingPhoto] = React.useState(false);

  function toggleListItem(field, value) {
    var arr = form[field] || [];
    var next = arr.indexOf(value) >= 0 ? arr.filter(function(x) { return x !== value; }) : arr.concat([value]);
    var upd = {}; upd[field] = next;
    setForm(Object.assign({}, form, upd));
  }

  function addCustomItem(field, customField) {
    var v = (form[customField] || '').trim();
    if (!v) return;
    var arr = form[field] || [];
    if (arr.indexOf(v) < 0) arr = arr.concat([v]);
    var upd = {}; upd[field] = arr; upd[customField] = '';
    setForm(Object.assign({}, form, upd));
  }

  // --- Idées d'activités selon les spécialités du guide ---
  var [showIdeas, setShowIdeas] = React.useState(false);
  var [ideas, setIdeas] = React.useState(null);

  function openIdeas() {
    setShowIdeas(true);
    if (ideas) return;
    axios.get(API_URL + '/api/activity-templates', { withCredentials: true })
      .then(function(res) { setIdeas((res.data && res.data.templates) || {}); })
      .catch(function() { setIdeas({}); });
  }

  function applyIdea(tpl) {
    setForm(Object.assign({}, form, {
      name_fr: tpl.name_fr || form.name_fr,
      name_en: tpl.name_en || form.name_en,
      description_fr: tpl.desc_fr || form.description_fr,
      description_en: tpl.desc_en || form.description_en,
      duration_hours: tpl.duration_hours || form.duration_hours,
      adult_rate: tpl.adult_rate || form.adult_rate,
      child_rate: tpl.child_rate || form.child_rate,
      included: tpl.included || form.included,
      not_included: tpl.not_included || form.not_included
    }));
    setShowIdeas(false);
    toast.success(t('hostDash.form.ideaApplied'));
  }

  // --- Bibliothèque de photos prêtes à l'emploi ---
  var [showLibrary, setShowLibrary] = React.useState(false);
  var [library, setLibrary] = React.useState(null);

  function openLibrary() {
    setShowLibrary(true);
    if (library) return;
    axios.get(API_URL + '/api/photo-library', { params: { location: form.location || '' } })
      .then(function(res) { setLibrary((res.data && res.data.library) || {}); })
      .catch(function() { setLibrary({}); });
  }

  function addFromLibrary(url) {
    if (photos.length >= 5) { toast.error(t('hostDash.form.max5photos')); return; }
    if (photos.indexOf(url) >= 0) { toast.info(t('hostDash.form.photoAlready')); return; }
    var next = photos.concat([url]);
    setPhotos(next);
    persistPhotos(next);
    toast.success(t('hostDash.form.photoAdded'));
  }

  function persistPhotos(list) {
    if (activity && activity.activity_id) {
      axios.put(API_URL + '/api/guides/me/activities/' + activity.activity_id + '/photos', { photos: list }, { withCredentials: true }).catch(function() {});
    }
  }

  function handlePhotoUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    if (photos.length >= 5) { toast.error(t('hostDash.form.max5photos')); return; }
    setUploadingPhoto(true);
    uploadFile(file)
      .then(function(url) {
        var next = photos.concat([url]);
        setPhotos(next);
        persistPhotos(next);
        setUploadingPhoto(false);
        toast.success(t('hostDash.form.photoAdded'));
      })
      .catch(function() { setUploadingPhoto(false); toast.error(t('hostDash.form.uploadFail')); });
    e.target.value = '';
  }

  function removePhoto(idx) {
    var next = [];
    for (var i = 0; i < photos.length; i++) { if (i !== idx) next.push(photos[i]); }
    setPhotos(next);
    persistPhotos(next);
  }

  function toggleDay(day) {
    var newDays = [];
    var found = false;
    for (var i = 0; i < form.days_of_week.length; i++) {
      if (form.days_of_week[i] === day) { found = true; } else { newDays.push(form.days_of_week[i]); }
    }
    if (!found) newDays.push(day);
    setForm(Object.assign({}, form, { days_of_week: newDays }));
  }

  // ---------- Helpers Séjour ----------
  var isSejour = form.activity_type === 'sejour';
  var [itinLang, setItinLang] = React.useState('fr'); // langue éditée dans l'itinéraire

  function setField(k, v) { var u = {}; u[k] = v; setForm(Object.assign({}, form, u)); }

  function addDestination() {
    var v = (form.destination_input || '').trim();
    if (!v) return;
    var arr = form.destinations || [];
    if (arr.indexOf(v) < 0) arr = arr.concat([v]);
    setForm(Object.assign({}, form, { destinations: arr, destination_input: '' }));
  }
  function removeDestination(idx) {
    setField('destinations', (form.destinations || []).filter(function(_, i) { return i !== idx; }));
  }

  // Grille de prix forfait : une ligne par taille de groupe (min..max).
  function packageRows() {
    var rows = [];
    var mn = parseInt(form.min_participants, 10) || 1;
    var mx = parseInt(form.max_participants, 10) || mn;
    for (var n = mn; n <= mx; n++) rows.push(n);
    return rows;
  }
  function setPackagePrice(size, value) {
    var pp = Object.assign({}, form.package_prices || {});
    pp[String(size)] = parseInt(value, 10) || 0;
    setField('package_prices', pp);
  }
  // Pré-remplit la grille : base pour le minimum, +X% suggéré par personne en plus.
  function autofillPackage() {
    // Base = premier palier renseigne (plus forcement celui du minimum, qui peut
    // rester vide depuis que la grille se lit par paliers).
    var base = 0;
    var rowsA = packageRows();
    for (var i = 0; i < rowsA.length && !base; i++) base = parseInt(form.package_prices[String(rowsA[i])], 10) || 0;
    if (!base) { toast.error(t('hostDash.sejour.setBaseFirst')); return; }
    var pp = Object.assign({}, form.package_prices || {});
    var mn = parseInt(form.min_participants, 10) || 1;
    packageRows().forEach(function(n) {
      var extra = n - mn;                 // personnes au-dessus du minimum
      var pct = extra * 10;               // +10% par personne en plus (modifiable ensuite)
      pp[String(n)] = Math.round(base * (1 + pct / 100));
    });
    setField('package_prices', pp);
    toast.success(t('hostDash.sejour.gridFilled'));
  }

  // Construit une liste d'itinéraire (jour 1..d), en conservant les jours déjà saisis.
  function buildItinerary(days, cur) {
    var d = parseInt(days, 10) || 0;
    var arr = cur || [];
    var next = [];
    for (var i = 1; i <= d; i++) {
      var existing = arr.find(function(x) { return x.day === i; });
      next.push(existing || { day: i, location: '', title: { fr: '', en: '', jp: '' }, description: { fr: '', en: '', jp: '' } });
    }
    return next;
  }
  // Itinéraire : garde une ligne par jour (1..duration_days).
  function syncItinerary(days) {
    var d = parseInt(days, 10) || 0;
    setForm(Object.assign({}, form, { duration_days: d, itinerary: buildItinerary(d, form.itinerary) }));
  }
  function setItineraryField(day, field, value) {
    var next = (form.itinerary || []).map(function(x) {
      if (x.day !== day) return x;
      if (field === 'location') return Object.assign({}, x, { location: value });
      // titre/description : on édite la langue actuellement sélectionnée (FR/EN/JP).
      var obj = Object.assign({}, x[field] || {}); obj[itinLang] = value;
      var u = {}; u[field] = obj;
      return Object.assign({}, x, u);
    });
    setField('itinerary', next);
  }

  // ---------- Suggestions de visites (catalogue) ----------
  var [suggestions, setSuggestions] = React.useState(null);
  var [suggestOpenDay, setSuggestOpenDay] = React.useState(null);

  function openSuggest(day) {
    setSuggestOpenDay(day);
    var cities = (form.destinations || []).join(',');
    axios.get(API_URL + '/api/sejour-suggestions', { params: { cities: cities }, withCredentials: true })
      .then(function(res) { setSuggestions((res.data && res.data.suggestions) || {}); })
      .catch(function() { setSuggestions({}); });
  }

  function applySuggestion(day, city, item) {
    var next = (form.itinerary || []).map(function(x) {
      if (x.day !== day) return x;
      return Object.assign({}, x, { location: city, title: item.title, description: item.description });
    });
    setForm(Object.assign({}, form, { itinerary: next }));
    setSuggestOpenDay(null);
    toast.success(t('hostDash.sejour.suggestionAdded'));
  }

  // ---------- Génération IA du séjour ----------
  var [aiLoading, setAiLoading] = React.useState(false);
  function generateWithAI() {
    // Inclut une ville tapée mais pas encore "ajoutée" (piège fréquent).
    var dests = (form.destinations || []).slice();
    var pending = (form.destination_input || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
    pending.forEach(function(p) { if (dests.indexOf(p) < 0) dests.push(p); });
    if (!dests.length) { toast.error(t('hostDash.sejour.aiNeedCity')); return; }
    setAiLoading(true);
    axios.post(API_URL + '/api/guides/me/activities/generate-sejour', {
      destinations: dests, duration_days: parseInt(form.duration_days, 10) || 3,
      theme: form.ai_instructions || '', min_participants: parseInt(form.min_participants, 10) || 2
    }, { withCredentials: true })
      .then(function(res) {
        var d = res.data || {};
        var it = d.itinerary || form.itinerary;
        // Pré-remplit la grille de prix à partir de la suggestion IA (+10%/pers).
        var pp = Object.assign({}, form.package_prices || {});
        var base = parseInt(d.suggested_price_min, 10) || 0;
        if (base) {
          var mn = parseInt(form.min_participants, 10) || 2;
          var mx = parseInt(form.max_participants, 10) || mn;
          for (var n = mn; n <= mx; n++) { pp[String(n)] = Math.round(base * (1 + (n - mn) * 10 / 100)); }
        }
        setForm(Object.assign({}, form, {
          destinations: dests,
          destination_input: '',
          pricing_mode: 'forfait',
          package_prices: pp,
          name_fr: (d.name && d.name.fr) || form.name_fr,
          name_en: (d.name && d.name.en) || form.name_en,
          name_jp: (d.name && d.name.jp) || form.name_jp,
          description_fr: (d.description && d.description.fr) || form.description_fr,
          description_en: (d.description && d.description.en) || form.description_en,
          description_jp: (d.description && d.description.jp) || form.description_jp,
          included: d.included || form.included,
          not_included: d.not_included || form.not_included,
          itinerary: it,
          duration_days: (it && it.length) || form.duration_days
        }));
        setAiLoading(false);
        toast.success(t('hostDash.sejour.aiDone'));
      })
      .catch(function(err) { setAiLoading(false); toast.error(apiErr(err, t('hostDash.sejour.aiError'))); });
  }

  // ---------- Génération IA d'une sortie journée ----------
  function generateActivityAI() {
    if (!form.location.trim()) { toast.error(t('hostDash.form.locationRequired')); return; }
    setAiLoading(true);
    axios.post(API_URL + '/api/guides/me/activities/generate-activity', {
      location: form.location, theme: form.ai_instructions || ''
    }, { withCredentials: true })
      .then(function(res) {
        var d = res.data || {};
        setForm(Object.assign({}, form, {
          name_fr: (d.name && d.name.fr) || form.name_fr,
          name_en: (d.name && d.name.en) || form.name_en,
          name_jp: (d.name && d.name.jp) || form.name_jp,
          description_fr: (d.description && d.description.fr) || form.description_fr,
          description_en: (d.description && d.description.en) || form.description_en,
          description_jp: (d.description && d.description.jp) || form.description_jp,
          included: d.included || form.included,
          not_included: d.not_included || form.not_included
        }));
        setAiLoading(false);
        toast.success(t('hostDash.sejour.aiDone'));
      })
      .catch(function(err) { setAiLoading(false); toast.error(apiErr(err, t('hostDash.sejour.aiError'))); });
  }

  // ---------- Zoom photo (lightbox) ----------
  var [zoomPhoto, setZoomPhoto] = React.useState(null);

  // ---------- Génération d'une photo par IA ----------
  var [imgLoading, setImgLoading] = React.useState(false);
  function generatePhotoAI() {
    if (photos.length >= 5) { toast.error(t('hostDash.form.max5photos')); return; }
    setImgLoading(true);
    axios.post(API_URL + '/api/generate-image', {
      name: form.name_fr || form.name_en || form.name_jp || '',
      destinations: form.destinations || [],
      location: form.location || '',
      activity_type: form.activity_type || 'journee'
    }, { withCredentials: true })
      .then(function(res) {
        var url = (res.data && res.data.url) || '';
        if (url && url.indexOf('http') !== 0) url = API_URL + url;
        if (url) { var next = photos.concat([url]); setPhotos(next); persistPhotos(next); }
        setImgLoading(false);
        toast.success(t('hostDash.form.aiPhotoDone'));
      })
      .catch(function(err) { setImgLoading(false); toast.error(apiErr(err, t('hostDash.form.aiPhotoError'))); });
  }

  function handleSave() {
    if (!form.name_fr.trim() && !form.name_en.trim() && !form.name_jp.trim()) { toast.error(t('hostDash.form.nameRequired')); return; }
    var out = Object.assign({}, form, { photos: photos });
    if (isSejour) {
      if (!(form.destinations || []).length) { toast.error(t('hostDash.sejour.destRequired')); return; }
      if (form.pricing_mode === 'forfait') {
        // La grille se lit par PALIERS (« jusqu'a N personnes ») : une ligne vide
        // reprend le palier suivant. Seul le plus grand palier est donc
        // obligatoire — c'est lui qui garantit qu'aucune taille de groupe
        // autorisee ne se retrouve sans tarif.
        var rows = packageRows();
        if (!form.package_prices[String(rows[rows.length - 1])]) {
          toast.error(t('hostDash.sejour.maxRowRequired')); return;
        }
        // Purge les tailles hors fourchette min–max (ex. ancien prix "3" après passage à min 4).
        var pruned = {};
        packageRows().forEach(function(n) { if (form.package_prices[String(n)]) pruned[String(n)] = form.package_prices[String(n)]; });
        out.package_prices = pruned;
      }
      // Le lieu unique est déduit des destinations ; pas de jours récurrents.
      out.location = (form.location && form.location.trim()) || form.destinations.join(' • ');
      out.days_of_week = [];
    } else {
      if (!form.location.trim()) { toast.error(t('hostDash.form.locationRequired')); return; }
      if (form.days_of_week.length === 0) { toast.error(t('hostDash.form.selectDay')); return; }
    }
    setSaving(true);
    onSave(out, function() { setSaving(false); });
  }

  var locationSuggestions = [];
  for (var i = 0; i < POPULAR_LOCATIONS.length; i++) {
    locationSuggestions.push(<option key={POPULAR_LOCATIONS[i]} value={POPULAR_LOCATIONS[i]} />);
  }

  var dayCheckboxes = [];
  for (var d = 0; d < DAY_VALUES.length; d++) {
    var dayVal = DAY_VALUES[d];
    var isChecked = form.days_of_week.indexOf(dayVal) >= 0;
    dayCheckboxes.push(
      <label key={dayVal} className={'flex items-center gap-2 p-2 rounded-lg cursor-pointer border ' + (isChecked ? 'border-[#F9004D]/50 bg-[#F9004D]/10' : 'border-white/10 bg-zinc-800/30')}>
        <input type="checkbox" checked={isChecked} onChange={function(v) { return function() { toggleDay(v); }; }(dayVal)} className="accent-[#F9004D]" />
        <span className="text-zinc-300 text-sm">{t('hostDash.days.' + dayVal)}</span>
      </label>
    );
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid="activity-form">
      <h3 className="text-xl font-bold text-white mb-6">{isEdit ? t('hostDash.form.editTitle') : t('hostDash.form.newTitle')}</h3>
      <div className="space-y-6">
        {/* Idées prêtes à personnaliser, selon les spécialités du guide.
            Utile surtout à la création (évite la page blanche). Sorties journée
            uniquement : sans objet pour un séjour. */}
        {!isEdit && !isSejour && (
          <div>
            <Button type="button" variant="outline" onClick={openIdeas}
              className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
              data-testid="open-activity-ideas">
              <Sparkles className="h-4 w-4 mr-2" />
              {t('hostDash.form.ideasButton')}
            </Button>

            {showIdeas && (
              <div className="mt-3 bg-black/30 border border-white/10 rounded-xl p-4" data-testid="activity-ideas">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-zinc-300 text-sm">{t('hostDash.form.ideasHint')}</p>
                  <button type="button" onClick={function() { setShowIdeas(false); }}
                    className="text-zinc-400 hover:text-white" data-testid="close-activity-ideas">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {!ideas ? (
                  <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 text-[#F9004D] animate-spin" /></div>
                ) : (
                  <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
                    {Object.keys(ideas).map(function(spec) {
                      return (
                        <div key={spec}>
                          <p className="text-zinc-400 text-xs uppercase tracking-wide mb-2">{ideas[spec].label}</p>
                          <div className="space-y-2">
                            {ideas[spec].templates.map(function(tpl, i) {
                              return (
                                <button type="button" key={spec + i} onClick={function() { applyIdea(tpl); }}
                                  className="w-full text-left p-3 rounded-lg border border-white/10 hover:border-[#00F0FF] hover:bg-white/5 transition-colors">
                                  <p className="text-white text-sm font-semibold">{tpl.name_fr}</p>
                                  <p className="text-zinc-400 text-xs mt-1 line-clamp-2">{tpl.desc_fr}</p>
                                  <p className="text-zinc-500 text-xs mt-1">
                                    {tpl.duration_hours}h · {formatYen(tpl.adult_rate)} {t('hostDash.form.perAdult')}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Type d'activité : sortie à la journée OU séjour multi-jours */}
        <div>
          <Label className="text-zinc-300 mb-2 block">{t('hostDash.sejour.typeLabel')}</Label>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={function() { setField('activity_type', 'journee'); }}
              className={'px-4 py-3 rounded-lg border text-sm font-semibold ' + (!isSejour ? 'border-[#F9004D] bg-[#F9004D]/10 text-white' : 'border-white/10 text-zinc-400')}
              data-testid="type-journee">{t('hostDash.sejour.typeDay')}</button>
            <button type="button" onClick={function() {
                var days = parseInt(form.duration_days, 10) || 3;
                var it = (form.itinerary && form.itinerary.length) ? form.itinerary : buildItinerary(days, form.itinerary);
                setForm(Object.assign({}, form, { activity_type: 'sejour', duration_days: days, itinerary: it }));
              }}
              className={'px-4 py-3 rounded-lg border text-sm font-semibold ' + (isSejour ? 'border-[#F9004D] bg-[#F9004D]/10 text-white' : 'border-white/10 text-zinc-400')}
              data-testid="type-sejour">{t('hostDash.sejour.typeStay')}</button>
          </div>
        </div>

        {isSejour && (
          <div className="space-y-5 border border-[#F9004D]/30 rounded-xl p-4 bg-[#F9004D]/5" data-testid="sejour-section">
            {/* Assistant IA (Phase 2) */}
            <div>
              <p className="text-zinc-300 text-sm mb-2">{t('hostDash.sejour.aiHint')}</p>
              <textarea value={form.ai_instructions} onChange={function(e) { setField('ai_instructions', e.target.value); }}
                placeholder={t('hostDash.sejour.aiInstructionsPlaceholder')} rows={2}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-2" data-testid="sejour-ai-instructions" />
              <button type="button" onClick={generateWithAI} disabled={aiLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F9004D] to-[#00F0FF] text-black font-bold text-sm disabled:opacity-60"
                data-testid="sejour-ai-btn">{aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : '✨'} {aiLoading ? t('hostDash.sejour.aiGenerating') : t('hostDash.sejour.aiButton')}</button>
              {aiLoading && <TokyoRailLoader label={t('hostDash.sejour.aiGenerating')} />}
            </div>

            {/* Destinations */}
            <div>
              <Label className="text-zinc-300 mb-2 block">{t('hostDash.sejour.destinations')}</Label>
              <div className="flex gap-2">
                <Input value={form.destination_input} onChange={function(e) { setField('destination_input', e.target.value); }}
                  onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); addDestination(); } }}
                  placeholder={t('hostDash.sejour.destinationsPlaceholder')} className="bg-black/20 border-white/10 text-white" />
                <button type="button" onClick={addDestination} className="px-4 rounded-lg bg-white/10 text-white">{t('hostDash.form.add')}</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(form.destinations || []).map(function(d, idx) {
                  return <span key={d + idx} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 border border-white/10 text-sm text-white">
                    {d}<button type="button" onClick={function() { removeDestination(idx); }} className="text-red-400">×</button></span>;
                })}
              </div>
            </div>

            {/* Durée */}
            <div>
              <Label className="text-zinc-300 mb-2 block">{t('hostDash.sejour.duration')}</Label>
              <Input type="number" min="1" value={form.duration_days}
                onChange={function(e) { syncItinerary(e.target.value); }}
                className="bg-black/20 border-white/10 text-white w-32" data-testid="sejour-duration" />
            </div>

            {/* Mode de tarif */}
            <div>
              <Label className="text-zinc-300 mb-2 block">{t('hostDash.sejour.pricingMode')}</Label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={function() { setField('pricing_mode', 'par_jour'); }}
                  className={'px-4 py-2 rounded-lg border text-sm ' + (form.pricing_mode !== 'forfait' ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-white' : 'border-white/10 text-zinc-400')}>{t('hostDash.sejour.perDay')}</button>
                <button type="button" onClick={function() { setField('pricing_mode', 'forfait'); }}
                  className={'px-4 py-2 rounded-lg border text-sm ' + (form.pricing_mode === 'forfait' ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-white' : 'border-white/10 text-zinc-400')}>{t('hostDash.sejour.package')}</button>
              </div>
            </div>

            {/* Grille de prix forfait */}
            {form.pricing_mode === 'forfait' && (
              <div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-zinc-300 mb-1 block text-xs">{t('hostDash.sejour.minParticipants')}</Label>
                    <Input type="number" min="1" value={form.min_participants}
                      onChange={function(e) { setField('min_participants', parseInt(e.target.value, 10) || 1); }}
                      className="bg-black/20 border-white/10 text-white" />
                    <p className="text-zinc-500 text-[11px] mt-1">{t('hostDash.sejour.minParticipantsHint')}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-300 mb-1 block text-xs">{t('hostDash.form.maxParticipants')}</Label>
                    <Input type="number" min="1" value={form.max_participants}
                      onChange={function(e) { setForm(Object.assign({}, form, { max_participants: parseInt(e.target.value, 10) || 1 })); }}
                      className="bg-black/20 border-white/10 text-white" />
                  </div>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-zinc-300 block">{t('hostDash.sejour.priceGrid')}</Label>
                  <button type="button" onClick={autofillPackage} className="text-xs text-[#00F0FF] underline">{t('hostDash.sejour.autofill')}</button>
                </div>
                <div className="space-y-2">
                  {packageRows().map(function(n) {
                    return <div key={n} className="flex items-center gap-3">
                      <span className="text-zinc-300 text-sm w-28">{t('hostDash.sejour.upToNpeople', { count: n })}</span>
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">¥</span>
                        <Input type="number" min="0" value={form.package_prices[String(n)] || ''}
                          onChange={function(e) { setPackagePrice(n, e.target.value); }}
                          placeholder="0" className="bg-black/20 border-white/10 text-white pl-7" data-testid={'pkg-price-' + n} />
                      </div>
                      <span className="text-zinc-400 text-xs w-24 text-right">{formatMoney(form.package_prices[String(n)] || 0, 'JPY')}</span>
                    </div>;
                  })}
                </div>
                <p className="text-zinc-500 text-xs mt-2">{t('hostDash.sejour.priceGridHint')}</p>
              </div>
            )}

            {/* Itinéraire jour par jour */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-zinc-300 block">{t('hostDash.sejour.itinerary')}</Label>
                <div className="flex gap-1" data-testid="itin-lang-switch">
                  {['fr', 'en', 'jp'].map(function(lg) {
                    return <button key={lg} type="button" onClick={function() { setItinLang(lg); }}
                      className={'px-2 py-1 rounded text-xs font-bold ' + (itinLang === lg ? 'bg-[#F9004D] text-white' : 'bg-white/10 text-zinc-400')}>
                      {lg.toUpperCase()}</button>;
                  })}
                </div>
              </div>
              <p className="text-zinc-500 text-xs mb-2">{t('hostDash.sejour.langHint')}</p>
              <div className="space-y-3">
                {(form.itinerary || []).map(function(d) {
                  return <div key={d.day} className="border border-white/10 rounded-lg p-3 bg-black/20">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[#F9004D] font-bold text-sm">{t('hostDash.sejour.day', { day: d.day })}</p>
                      <button type="button" onClick={function() { if (suggestOpenDay === d.day) { setSuggestOpenDay(null); } else { openSuggest(d.day); } }}
                        className="text-xs text-[#00F0FF] underline" data-testid={'suggest-btn-' + d.day}>+ {t('hostDash.sejour.suggestion')}</button>
                    </div>
                    <Input value={d.location} onChange={function(e) { setItineraryField(d.day, 'location', e.target.value); }}
                      placeholder={t('hostDash.sejour.dayCity')} className="bg-black/20 border-white/10 text-white mb-2" />
                    <Input value={(d.title && d.title[itinLang]) || ''} onChange={function(e) { setItineraryField(d.day, 'title', e.target.value); }}
                      placeholder={t('hostDash.sejour.dayTitle')} className="bg-black/20 border-white/10 text-white mb-2" />
                    <textarea value={(d.description && d.description[itinLang]) || ''} onChange={function(e) { setItineraryField(d.day, 'description', e.target.value); }}
                      placeholder={t('hostDash.sejour.dayDesc')} rows={2}
                      className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
                    {suggestOpenDay === d.day && (
                      <div className="mt-2 border border-[#00F0FF]/30 rounded-lg p-2 bg-black/40 max-h-64 overflow-y-auto" data-testid="suggest-panel">
                        {suggestions === null ? (
                          <p className="text-zinc-400 text-xs">…</p>
                        ) : Object.keys(suggestions).length === 0 ? (
                          <p className="text-zinc-400 text-xs">{t('hostDash.sejour.noSuggestion')}</p>
                        ) : (
                          Object.keys(suggestions).map(function(city) {
                            return <div key={city} className="mb-2">
                              <p className="text-zinc-400 text-xs font-bold mb-1">{city}</p>
                              {suggestions[city].map(function(item, ii) {
                                return <button key={ii} type="button" onClick={function() { applySuggestion(d.day, city, item); }}
                                  className="block w-full text-left px-2 py-1 rounded hover:bg-white/10 text-white text-sm">
                                  {(item.title && item.title.fr) || ''}</button>;
                              })}
                            </div>;
                          })
                        )}
                      </div>
                    )}
                  </div>;
                })}
              </div>
            </div>
          </div>
        )}

        {!isSejour && (
        <div>
          <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.locationLabel')}</Label>
          <Input value={form.location} onChange={function(e) { setForm(Object.assign({}, form, { location: e.target.value })); }}
            list="location-suggestions" placeholder={t('hostDash.form.locationPlaceholder')}
            className="bg-black/20 border-white/10 text-white" />
          <datalist id="location-suggestions">{locationSuggestions}</datalist>
          <p className="text-zinc-500 text-xs mt-1">{t('hostDash.form.locationHint')}</p>
          <button type="button" onClick={generateActivityAI} disabled={aiLoading}
            className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F9004D] to-[#00F0FF] text-black font-bold text-sm disabled:opacity-60"
            data-testid="activity-ai-btn">
            {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : '✨'} {aiLoading ? t('hostDash.sejour.aiGenerating') : t('hostDash.form.aiFillBtn')}
          </button>
          {aiLoading && <TokyoRailLoader label={t('hostDash.sejour.aiGenerating')} />}
        </div>
        )}

        <div>
          <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.meetingPoint')}</Label>
          <Input value={form.meeting_point} onChange={function(e) { setForm(Object.assign({}, form, { meeting_point: e.target.value })); }}
            placeholder={t('hostDash.form.meetingPointPlaceholder')}
            className="bg-black/20 border-white/10 text-white" data-testid="meeting-point-input" />
          <p className="text-zinc-500 text-xs mt-1">{t('hostDash.form.meetingPointHint')}</p>
        </div>

        <div>
          <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.meetingPointUrl')}</Label>
          <Input value={form.meeting_point_url} onChange={function(e) { setForm(Object.assign({}, form, { meeting_point_url: e.target.value })); }}
            placeholder={t('hostDash.form.meetingPointUrlPlaceholder')}
            className="bg-black/20 border-white/10 text-white" data-testid="meeting-point-url-input" />
          <p className="text-zinc-500 text-xs mt-1">{t('hostDash.form.meetingPointUrlHint')}</p>
        </div>

        {!isSejour && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.startTime')}</Label>
            <Input type="time" value={form.start_time} onChange={function(e) { setForm(Object.assign({}, form, { start_time: e.target.value })); }}
              style={{ colorScheme: 'dark' }} className="bg-black/20 border-white/10 text-white" data-testid="start-time-input" />
          </div>
          <div>
            <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.durationHours')}</Label>
            <Input type="number" min="1" max="24" value={form.duration_hours} onChange={function(e) { setForm(Object.assign({}, form, { duration_hours: parseInt(e.target.value) || 1 })); }}
              className="bg-black/20 border-white/10 text-white" data-testid="duration-input" />
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.nameFr')}</Label>
            <Input value={form.name_fr} onChange={function(e) { setForm(Object.assign({}, form, { name_fr: e.target.value })); }}
              className="bg-black/20 border-white/10 text-white" placeholder={t('hostDash.form.nameFrPlaceholder')} />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.nameEn')}</Label>
            <Input value={form.name_en} onChange={function(e) { setForm(Object.assign({}, form, { name_en: e.target.value })); }}
              className="bg-black/20 border-white/10 text-white" placeholder={t('hostDash.form.nameEnPlaceholder')} />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.nameJp')}</Label>
            <Input value={form.name_jp} onChange={function(e) { setForm(Object.assign({}, form, { name_jp: e.target.value })); }}
              className="bg-black/20 border-white/10 text-white" />
          </div>
        </div>

        <div>
          <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.descFr')}</Label>
          <textarea value={form.description_fr} onChange={function(e) { setForm(Object.assign({}, form, { description_fr: e.target.value })); }}
            className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white min-h-[100px]" placeholder={t('hostDash.form.descFrPlaceholder')} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.descEn')}</Label>
            <textarea value={form.description_en} onChange={function(e) { setForm(Object.assign({}, form, { description_en: e.target.value })); }}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white min-h-[80px]" />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.descJp')}</Label>
            <textarea value={form.description_jp} onChange={function(e) { setForm(Object.assign({}, form, { description_jp: e.target.value })); }}
              className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white min-h-[80px]" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.included')}</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {INCLUDED_SUGGESTIONS.map(function(opt) {
                var on = (form.included || []).indexOf(opt) >= 0;
                return (
                  <button type="button" key={opt} onClick={function() { toggleListItem('included', opt); }}
                    className={'px-3 py-1 rounded-full text-sm border transition-colors ' + (on ? 'bg-[#F9004D] border-[#F9004D] text-white' : 'border-white/20 text-zinc-300 hover:border-white/40')}>
                    {(on ? '✓ ' : '+ ') + itemLabel(t, opt)}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap">
              {(form.included || []).filter(function(x) { return INCLUDED_SUGGESTIONS.indexOf(x) < 0; }).map(function(x) {
                return (
                  <span key={x} className="inline-flex items-center gap-1 px-3 py-1 mr-2 mb-2 rounded-full text-sm bg-[#F9004D]/20 border border-[#F9004D]/40 text-white">
                    {itemLabel(t, x)}
                    <button type="button" onClick={function() { toggleListItem('included', x); }} className="text-white/70 hover:text-white">×</button>
                  </span>
                );
              })}
            </div>
            <div className="flex gap-2 mt-1">
              <Input value={form.included_custom} onChange={function(e) { setForm(Object.assign({}, form, { included_custom: e.target.value })); }}
                onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); addCustomItem('included', 'included_custom'); } }}
                placeholder={t('hostDash.form.addCustom')} className="bg-black/20 border-white/10 text-white" />
              <Button type="button" variant="outline" onClick={function() { addCustomItem('included', 'included_custom'); }}>{t('hostDash.form.add')}</Button>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 mb-2 block">{t('hostDash.form.notIncluded')}</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {NOT_INCLUDED_SUGGESTIONS.map(function(opt) {
                var on = (form.not_included || []).indexOf(opt) >= 0;
                return (
                  <button type="button" key={opt} onClick={function() { toggleListItem('not_included', opt); }}
                    className={'px-3 py-1 rounded-full text-sm border transition-colors ' + (on ? 'bg-zinc-600 border-zinc-500 text-white' : 'border-white/20 text-zinc-300 hover:border-white/40')}>
                    {(on ? '✓ ' : '+ ') + itemLabel(t, opt)}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap">
              {(form.not_included || []).filter(function(x) { return NOT_INCLUDED_SUGGESTIONS.indexOf(x) < 0; }).map(function(x) {
                return (
                  <span key={x} className="inline-flex items-center gap-1 px-3 py-1 mr-2 mb-2 rounded-full text-sm bg-zinc-600/40 border border-zinc-500/50 text-white">
                    {itemLabel(t, x)}
                    <button type="button" onClick={function() { toggleListItem('not_included', x); }} className="text-white/70 hover:text-white">×</button>
                  </span>
                );
              })}
            </div>
            <div className="flex gap-2 mt-1">
              <Input value={form.not_included_custom} onChange={function(e) { setForm(Object.assign({}, form, { not_included_custom: e.target.value })); }}
                onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); addCustomItem('not_included', 'not_included_custom'); } }}
                placeholder={t('hostDash.form.addCustom')} className="bg-black/20 border-white/10 text-white" />
              <Button type="button" variant="outline" onClick={function() { addCustomItem('not_included', 'not_included_custom'); }}>{t('hostDash.form.add')}</Button>
            </div>
          </div>
        </div>

        {!isSejour && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.adultRate')}</Label>
            <Input type="number" value={form.adult_rate} onChange={function(e) { setForm(Object.assign({}, form, { adult_rate: parseInt(e.target.value) || 0 })); }}
              className="bg-black/20 border-white/10 text-white" min="0" />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.childRate')}</Label>
            <Input type="number" value={form.child_rate} onChange={function(e) { setForm(Object.assign({}, form, { child_rate: parseInt(e.target.value) || 0 })); }}
              className="bg-black/20 border-white/10 text-white" min="0" disabled={!form.children_allowed} />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.maxParticipants')}</Label>
            <Input type="number" value={form.max_participants} onChange={function(e) { setForm(Object.assign({}, form, { max_participants: parseInt(e.target.value) || 1 })); }}
              className="bg-black/20 border-white/10 text-white" min="1" max="6" />
          </div>
        </div>
        )}

        {!isSejour && (
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={form.children_allowed} onChange={function() { setForm(Object.assign({}, form, { children_allowed: !form.children_allowed })); }}
            className="accent-[#F9004D] w-5 h-5" />
          <span className="text-zinc-300">{t('hostDash.form.childrenAllowed')}</span>
        </div>
        )}

        {/* Réductions de groupe progressives */}
        {!isSejour && (
        <div className="border border-white/10 rounded-lg p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.group_discount_enabled} onChange={function() {
              var enabled = !form.group_discount_enabled;
              var gd = form.group_discounts;
              if (enabled && Object.keys(gd).length === 0) gd = { '2': 5, '3': 7, '4': 10, '5': 12, '6': 15 };
              setForm(Object.assign({}, form, { group_discount_enabled: enabled, group_discounts: gd }));
            }} className="accent-[#F9004D] w-5 h-5" data-testid="group-discount-toggle" />
            <span className="text-zinc-300">{t('hostDash.form.groupDiscount')} <span className="text-[#00F0FF] text-xs">({t('hostDash.form.recommended')})</span></span>
          </label>
          <p className="text-zinc-500 text-xs mt-1 ml-8">{t('hostDash.form.groupDiscountHint')}</p>
          {form.group_discount_enabled && (
            <div className="mt-3 ml-8 space-y-2">
              {[2, 3, 4, 5, 6].map(function(n) {
                var key = String(n);
                var on = key in form.group_discounts;
                return (
                  <div key={n} className="flex items-center gap-3">
                    <label className="flex items-center gap-2 w-40 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={function() {
                        var gd = Object.assign({}, form.group_discounts);
                        if (on) { delete gd[key]; } else { gd[key] = ({ '2': 5, '3': 7, '4': 10, '5': 12, '6': 15 })[key]; }
                        setForm(Object.assign({}, form, { group_discounts: gd }));
                      }} className="accent-[#F9004D]" data-testid={'group-tier-' + n} />
                      <span className="text-zinc-300 text-sm">{t('hostDash.form.groupOf', { count: n })}</span>
                    </label>
                    {on && (
                      <div className="flex items-center gap-1">
                        <Input type="number" min="0" max="100" value={form.group_discounts[key]}
                          onChange={function(e) {
                            var gd = Object.assign({}, form.group_discounts);
                            gd[key] = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                            setForm(Object.assign({}, form, { group_discounts: gd }));
                          }}
                          className="bg-black/20 border-white/10 text-white w-20 h-8" />
                        <span className="text-zinc-400 text-sm">%</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {!isSejour && (
        <div>
          <Label className="text-zinc-300 mb-3 block">{t('hostDash.form.daysLabel')}</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{dayCheckboxes}</div>
        </div>
        )}

        <div>
          <Label className="text-zinc-300 mb-1 block">{t('hostDash.form.noticeLabel')}</Label>
          <div className="flex items-center gap-2">
            <Input type="number" min="0" value={form.min_notice_days}
              onChange={function(e) { setForm(Object.assign({}, form, { min_notice_days: parseInt(e.target.value, 10) || 0 })); }}
              className="bg-black/20 border-white/10 text-white w-24" data-testid="min-notice-days" />
            <span className="text-zinc-400 text-sm">{t('hostDash.form.noticeUnit')}</span>
          </div>
          <p className="text-zinc-500 text-xs mt-1">{t('hostDash.form.noticeHint')}</p>
        </div>

        <div>
          {isSejour && <Label className="text-zinc-300 mb-1 block">{t('hostDash.sejour.startWindow')}</Label>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300 mb-1 block">{isSejour ? t('hostDash.sejour.startFrom') : t('hostDash.form.startDate')}</Label>
              <DatePicker value={form.start_date} onChange={function(v) { setForm(Object.assign({}, form, { start_date: v })); }} />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 block">{isSejour ? t('hostDash.sejour.startUntil') : t('hostDash.form.endDate')}</Label>
              <DatePicker value={form.end_date} minDate={form.start_date || undefined} onChange={function(v) { setForm(Object.assign({}, form, { end_date: v })); }} />
            </div>
          </div>
          {isSejour && <p className="text-zinc-500 text-xs mt-1">{t('hostDash.sejour.startWindowHint')}</p>}
        </div>

        <div>
          <Label className="text-zinc-300 mb-3 block">{t('hostDash.form.photosLabel')}</Label>
          <div className="flex flex-wrap gap-3">
            {photos.map(function(url, idx) {
              return (
                <div key={url + idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/10" data-testid={'activity-photo-' + idx}>
                  <img src={url} alt={'photo ' + (idx + 1)} onClick={function() { setZoomPhoto(url); }}
                    className="w-full h-full object-cover cursor-zoom-in" />
                  <button type="button" onClick={function() { removePhoto(idx); }}
                    className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-red-400 hover:text-red-300" data-testid={'remove-photo-' + idx}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
            {photos.length < 5 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#F9004D]/50 text-zinc-400" data-testid="add-photo-label">
                {uploadingPhoto ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImageIcon className="h-6 w-6" />}
                <span className="text-xs mt-1">{uploadingPhoto ? '...' : t('hostDash.form.add')}</span>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} data-testid="activity-photo-input" />
              </label>
            )}
          </div>

          {zoomPhoto && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={function() { setZoomPhoto(null); }} data-testid="photo-zoom">
              <img src={zoomPhoto} alt="zoom" className="max-w-full max-h-full rounded-lg object-contain" />
              <button type="button" onClick={function() { setZoomPhoto(null); }}
                className="absolute top-4 right-4 bg-black/70 rounded-full p-2 text-white hover:text-[#F9004D]">
                <X className="h-6 w-6" />
              </button>
            </div>
          )}

          {/* Bibliothèque : photos prêtes à l'emploi, pas besoin d'en chercher */}
          {photos.length < 5 && (
            <Button type="button" variant="outline" onClick={openLibrary}
              className="mt-3 border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10"
              data-testid="open-photo-library">
              <ImageIcon className="h-4 w-4 mr-2" />
              {t('hostDash.form.chooseFromLibrary')}
            </Button>
          )}

          {photos.length < 5 && (
            <Button type="button" variant="outline" onClick={generatePhotoAI} disabled={imgLoading}
              className="mt-3 ml-2 border-[#F9004D]/40 text-[#F9004D] hover:bg-[#F9004D]/10 disabled:opacity-60"
              data-testid="generate-photo-ai">
              {imgLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
              {imgLoading ? t('hostDash.form.aiPhotoGenerating') : t('hostDash.form.aiPhotoBtn')}
            </Button>
          )}

          {showLibrary && (
            <div className="mt-4 bg-black/30 border border-white/10 rounded-xl p-4" data-testid="photo-library">
              <div className="flex items-center justify-between mb-3">
                <p className="text-zinc-300 text-sm">{t('hostDash.form.libraryHint')}</p>
                <button type="button" onClick={function() { setShowLibrary(false); }}
                  className="text-zinc-400 hover:text-white" data-testid="close-photo-library">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {!library ? (
                <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 text-[#F9004D] animate-spin" /></div>
              ) : (
                <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
                  {Object.keys(library).map(function(area) {
                    return (
                      <div key={area}>
                        <p className="text-zinc-400 text-xs uppercase tracking-wide mb-2">{area}</p>
                        <div className="flex flex-wrap gap-2">
                          {library[area].map(function(url, i) {
                            var already = photos.indexOf(url) >= 0;
                            return (
                              <button type="button" key={area + i} onClick={function() { addFromLibrary(url); }}
                                className={'relative w-20 h-20 rounded-lg overflow-hidden border transition-colors ' +
                                  (already ? 'border-[#F9004D]' : 'border-white/10 hover:border-[#00F0FF]')}>
                                <img src={url} alt={area} className="w-full h-full object-cover" loading="lazy" />
                                {already && (
                                  <span className="absolute inset-0 bg-black/60 flex items-center justify-center text-[#F9004D]">
                                    <Check className="h-5 w-5" />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <Button onClick={handleSave} disabled={saving} className="bg-[#F9004D] hover:bg-[#D00040] text-white px-8" data-testid="save-activity-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            {isEdit ? t('hostDash.form.save') : t('hostDash.form.create')}
          </Button>
          <Button onClick={onCancel} variant="outline" className="border-white/20 text-zinc-300" data-testid="cancel-activity-btn">
            <X className="h-4 w-4 mr-2" /> {t('hostDash.form.cancel')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ActivityCard({ activity, onEdit, onToggle, onSubmit, onDuplicate }) {
  var { t } = useTranslation();
  var status = activity.status || 'draft';
  var hasPending = activity.pending_changes && Object.keys(activity.pending_changes).length > 0;

  var avail = activity.availability || {};
  var daysArr = avail.days_of_week || [];
  var slotsArr = avail.time_slots || [];

  var slotLabels = [];
  for (var k = 0; k < slotsArr.length; k++) {
    slotLabels.push(t('hostDash.slots.' + slotsArr[k]));
  }

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid={'activity-card-' + activity.activity_id}>
      {status === 'pending_review' && (
        <div className="mb-4 bg-yellow-500/10 border border-yellow-500/40 rounded-lg p-3 flex items-center gap-2 text-yellow-300 text-sm" data-testid="in-review-banner">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{t('hostDash.card.inReview')}</span>
        </div>
      )}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#F9004D]/20 text-[#F9004D] rounded-full text-sm">{activity.location}</span>
            <span className={'px-3 py-1 rounded-full text-xs ' + (STATUS_COLORS[status] || STATUS_COLORS.draft)}>
              {t('hostDash.status.' + status, status)}
            </span>
            {hasPending && (
              <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs">{t('hostDash.card.pendingChanges')}</span>
            )}
          </div>
          <h3 className="text-xl font-bold text-white">{activity.name.fr || activity.name.en || t('hostDash.card.noName')}</h3>
          <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{activity.description.fr || activity.description.en || ''}</p>
        </div>
      </div>

      {activity.activity_type === 'sejour' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.package')}</p>
            <p className="text-[#00F0FF] font-bold">{sejourMinPrice(activity) ? t('hostDash.card.fromPrice', { price: formatYen(sejourMinPrice(activity)) }) : t('hostDash.card.notDefined')}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.max')}</p>
            <p className="text-white font-bold">{activity.max_participants} {t('hostDash.card.pers')}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.duration')}</p>
            <p className="text-white font-bold">{activity.duration_days ? t('hostDash.card.durationDays', { count: activity.duration_days }) : t('hostDash.card.notDefined')}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.adult')}</p>
            <p className="text-[#00F0FF] font-bold">{formatYen(activity.adult_rate)}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.child')}</p>
            <p className="text-[#D4AF37] font-bold">{activity.children_allowed ? formatYen(activity.child_rate) : t('hostDash.card.forbidden')}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.max')}</p>
            <p className="text-white font-bold">{activity.max_participants} {t('hostDash.card.pers')}</p>
          </div>
          <div>
            <p className="text-zinc-500 text-xs">{t('hostDash.card.daysWord')}</p>
            <p className="text-white text-sm">{daysArr.length > 0 ? t('hostDash.card.daysCount', { count: daysArr.length }) : t('hostDash.card.notDefined')}</p>
          </div>
        </div>
      )}

      {activity.activity_type !== 'sejour' && (
        <div className="flex items-center gap-4 text-sm text-zinc-400 mb-4 flex-wrap">
          <span className="flex items-center gap-2 text-[#00F0FF]"><Clock className="h-4 w-4" />{t('hostDash.card.timeLabel')} : {activity.start_time || '—'}</span>
          {activity.duration_hours && (
            <span>{t('hostDash.card.durationLabel')} : {t('hostDash.card.durationValue', { count: activity.duration_hours })}</span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={function() { onEdit(activity); }} variant="outline" size="sm" className="border-white/20 text-zinc-300 hover:bg-white/10" data-testid={'edit-activity-' + activity.activity_id}>
          <Pencil className="h-4 w-4 mr-1" /> {t('hostDash.card.edit')}
        </Button>
        <Button onClick={function() { onDuplicate(activity.activity_id); }} variant="outline" size="sm" className="border-white/20 text-zinc-300 hover:bg-white/10" data-testid={'duplicate-activity-' + activity.activity_id}>
          <Plus className="h-4 w-4 mr-1" /> {t('hostDash.card.duplicate')}
        </Button>
        {status === 'draft' && (
          <Button onClick={function() { onSubmit(activity.activity_id); }} size="sm" className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" data-testid={'submit-activity-' + activity.activity_id}>
            <Send className="h-4 w-4 mr-1" /> {t('hostDash.card.submit')}
          </Button>
        )}
        {(status === 'approved' || status === 'inactive') && (
          <Button onClick={function() { onToggle(activity.activity_id); }} variant="outline" size="sm"
            className={status === 'approved' ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-green-500/50 text-green-400 hover:bg-green-500/10'}
            data-testid={'toggle-activity-' + activity.activity_id}>
            {status === 'approved' ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            {status === 'approved' ? t('hostDash.card.deactivate') : t('hostDash.card.reactivate')}
          </Button>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-zinc-400 text-xs mb-1">{t('hostDash.card.videoPromo')}</p>
        <VideoUpload activityId={activity.activity_id} label={t('hostDash.card.addVideoPromo')} />
      </div>
    </Card>
  );
}

function daysUntil(startDate) {
  if (!startDate) return null;
  var d = new Date(startDate + 'T00:00:00+09:00'); // Tokyo
  var nowParts = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' }); // YYYY-MM-DD
  var today = new Date(nowParts + 'T00:00:00+09:00');
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

function bookingActivityName(b, fallback) {
  var n = b && b.activity_name;
  if (n && typeof n === 'object') return n.fr || n.en || n.jp || fallback;
  if (n) return n;
  if (b && b.experience_title && b.experience_title.fr) return b.experience_title.fr;
  return fallback;
}

// Calendrier mensuel maison : jours réservés en évidence + compteur.
function MonthCalendar({ byDate, initialMonth, selectedKey, onSelect }) {
  var { i18n } = useTranslation();
  var loc = dateLocale(i18n.language);
  var start = initialMonth || new Date();
  var ymState = React.useState({ y: start.getFullYear(), m: start.getMonth() });
  var ym = ymState[0];
  var setYm = ymState[1];
  React.useEffect(function() {
    if (initialMonth) setYm({ y: initialMonth.getFullYear(), m: initialMonth.getMonth() });
  }, [initialMonth && initialMonth.getTime()]);

  function pad(n) { return ('0' + n).slice(-2); }
  function key(y, m, d) { return y + '-' + pad(m + 1) + '-' + pad(d); }

  var first = new Date(ym.y, ym.m, 1);
  var startWeekday = (first.getDay() + 6) % 7; // lundi = 0
  var daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  var monthLabel = first.toLocaleDateString(loc, { month: 'long', year: 'numeric' });
  // Abréviations des jours dans la langue de l'utilisateur (semaine démarrant lundi).
  var WD = [];
  for (var wd = 0; wd < 7; wd++) {
    // 2024-01-01 était un lundi : base fiable pour générer les 7 libellés.
    WD.push(new Date(Date.UTC(2024, 0, 1 + wd)).toLocaleDateString(loc, { weekday: 'short', timeZone: 'UTC' }));
  }

  var cells = [];
  var i;
  for (i = 0; i < startWeekday; i++) cells.push(<div key={'e' + i} />);
  for (i = 1; i <= daysInMonth; i++) {
    (function(dnum) {
      var kk = key(ym.y, ym.m, dnum);
      var count = (byDate[kk] || []).length;
      var sel = kk === selectedKey;
      cells.push(
        <button key={kk} type="button" onClick={function() { onSelect(new Date(ym.y, ym.m, dnum)); }}
          data-testid={'calday-' + kk}
          className={'h-12 rounded-md flex flex-col items-center justify-center text-sm ' +
            (sel ? 'ring-2 ring-[#00F0FF] ' : '') +
            (count > 0 ? 'bg-[#F9004D] text-white font-bold' : 'text-zinc-300 hover:bg-zinc-800')}>
          <span>{dnum}</span>
          {count > 0 && <span className="text-[10px] leading-none mt-0.5">{count}</span>}
        </button>
      );
    })(i);
  }

  function shift(delta) { var d = new Date(ym.y, ym.m + delta, 1); setYm({ y: d.getFullYear(), m: d.getMonth() }); }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={function() { shift(-1); }} className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 rounded-md text-white">‹</button>
        <span className="text-white font-bold capitalize">{monthLabel}</span>
        <button type="button" onClick={function() { shift(1); }} className="h-8 w-8 bg-zinc-800 hover:bg-zinc-700 rounded-md text-white">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">{WD.map(function(w) { return <div key={w} className="text-center text-xs text-zinc-500">{w}</div>; })}</div>
      <div className="grid grid-cols-7 gap-1">{cells}</div>
    </div>
  );
}

function HostBookings() {
  var { t } = useTranslation();
  var [bookings, setBookings] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [confirmCancel, setConfirmCancel] = React.useState(null); // booking
  var [cancelling, setCancelling] = React.useState(false);
  var [view, setView] = React.useState('upcoming'); // upcoming | list | calendar
  var [selectedDay, setSelectedDay] = React.useState(null);

  function fetchBookings() {
    axios.get(API_URL + '/api/bookings/v2/me/guide', { withCredentials: true })
      .then(function(res) { setBookings(res.data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  React.useEffect(function() { fetchBookings(); }, []);

  function doCancel() {
    if (!confirmCancel) return;
    setCancelling(true);
    axios.post(API_URL + '/api/bookings/v2/' + confirmCancel.booking_id + '/host-cancel', {}, { withCredentials: true })
      .then(function(res) { toast.success(res.data.message || t('hostDash.bookings.cancelSuccess')); setConfirmCancel(null); fetchBookings(); })
      .catch(function(e) { toast.error(e.response?.data?.detail || t('hostDash.bookings.cancelError')); })
      .finally(function() { setCancelling(false); });
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;

  if (bookings.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-white/10 p-8 text-center">
        <Calendar className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
        <h3 className="text-xl text-white mb-2">{t('hostDash.bookings.none')}</h3>
        <p className="text-zinc-400">{t('hostDash.bookings.noneDesc')}</p>
      </Card>
    );
  }

  function renderCard(b) {
    var statusClass = b.booking_status === 'confirmed' ? 'bg-green-500/20 text-green-400' : b.booking_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : b.booking_status === 'modified' ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400';
    var dleft = daysUntil(b.start_date);
    var canCancel = (b.booking_status === 'confirmed' || b.booking_status === 'modified') && dleft !== null && dleft >= 1;
    var sameDay = dleft === 0;
    return (
      <Card key={b.booking_id} className="bg-zinc-800/50 border-white/10 p-4">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <span className={'px-2 py-1 rounded text-xs ' + statusClass}>{t('travelerDash.status.' + b.booking_status, b.booking_status)}</span>
            <h4 className="text-white font-bold mt-2">{bookingActivityName(b, t('hostDash.msg.activity'))}</h4>
            <p className="text-zinc-400 text-sm">{b.traveler_name} - {b.start_date}{b.activity_start_time ? ' · ' + b.activity_start_time : ''}</p>
            <p className="text-zinc-500 text-sm">{t('hostDash.bookings.summary', { days: b.number_of_days, participants: b.participants?.length || 0 })}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-[#F9004D] font-bold text-xl">{formatYen(b.total_price)}</p>
            {canCancel && (
              <Button onClick={(function(bk) { return function() { setConfirmCancel(bk); }; })(b)}
                variant="outline" className="mt-2 border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs py-1 px-3 h-auto">
                <X className="h-3 w-3 mr-1" /> {t('hostDash.bookings.cancel')}
              </Button>
            )}
            {sameDay && (b.booking_status === 'confirmed' || b.booking_status === 'modified') && (
              <p className="text-xs text-zinc-500 mt-2 max-w-[160px]">{t('hostDash.bookings.sameDayWarn')}</p>
            )}
          </div>
        </div>
      </Card>
    );
  }

  // Dates -> réservations (pour la vue calendrier) + liste "à venir".
  function bookingDates(b) {
    var out = []; var base = new Date(b.start_date + 'T00:00:00');
    for (var i = 0; i < (b.number_of_days || 1); i++) { var d = new Date(base); d.setDate(base.getDate() + i); out.push(d); }
    return out;
  }
  var bookedDateObjs = [];
  var byDate = {};
  bookings.forEach(function(b) {
    if (b.booking_status === 'cancelled') return;
    bookingDates(b).forEach(function(d) {
      bookedDateObjs.push(d);
      var k = (function(x){ var y=x.getFullYear();var m=('0'+(x.getMonth()+1)).slice(-2);var dd=('0'+x.getDate()).slice(-2);return y+'-'+m+'-'+dd; })(d);
      (byDate[k] = byDate[k] || []).push(b);
    });
  });
  var todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
  var upcoming = bookings.filter(function(b) {
    return (b.booking_status === 'confirmed' || b.booking_status === 'modified') && b.start_date >= todayStr;
  }).sort(function(a, b) { return (a.start_date || '').localeCompare(b.start_date || ''); });

  var selKey = selectedDay ? (function(x){ var y=x.getFullYear();var m=('0'+(x.getMonth()+1)).slice(-2);var dd=('0'+x.getDate()).slice(-2);return y+'-'+m+'-'+dd; })(selectedDay) : null;
  var dayBookings = selKey ? (byDate[selKey] || []) : [];

  // Le calendrier s'ouvre sur le mois de la prochaine réservation (sinon aujourd'hui).
  var calendarFocus = upcoming.length ? new Date(upcoming[0].start_date + 'T00:00:00')
    : (bookedDateObjs.length ? new Date(Math.min.apply(null, bookedDateObjs.map(function(d) { return d.getTime(); }))) : undefined);

  var VIEWS = ['list', 'upcoming', 'calendar'];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('hostDash.bookings.title', { count: bookings.length })}</h2>
        <div className="flex gap-2">
          {VIEWS.map(function(v) {
            return (
              <Button key={v} onClick={function() { setView(v); }} size="sm"
                variant={view === v ? 'default' : 'outline'}
                className={view === v ? 'bg-[#F9004D] text-white' : 'border-white/20 text-zinc-300'}
                data-testid={'bookings-view-' + v}>
                {t('hostDash.bookings.view.' + v)}
              </Button>
            );
          })}
        </div>
      </div>

      {view === 'list' && <div className="space-y-4">{bookings.map(renderCard)}</div>}

      {view === 'upcoming' && (
        upcoming.length > 0
          ? <div className="space-y-4">{upcoming.map(renderCard)}</div>
          : <Card className="bg-zinc-900/50 border-white/10 p-8 text-center text-zinc-400">{t('hostDash.bookings.upcomingNone')}</Card>
      )}

      {view === 'calendar' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-zinc-900/50 border-white/10 p-4">
            <MonthCalendar byDate={byDate} initialMonth={calendarFocus} selectedKey={selKey} onSelect={setSelectedDay} />
          </Card>
          <div className="space-y-3">
            {!selectedDay
              ? <Card className="bg-zinc-900/50 border-white/10 p-6 text-center text-zinc-400">{t('hostDash.bookings.calendarPick')}</Card>
              : dayBookings.length > 0
                ? dayBookings.map(renderCard)
                : <Card className="bg-zinc-900/50 border-white/10 p-6 text-center text-zinc-400">{t('hostDash.bookings.calendarDayNone')}</Card>}
          </div>
        </div>
      )}

      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={function() { if (!cancelling) setConfirmCancel(null); }}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl" onClick={function(e) { e.stopPropagation(); }}>
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{t('hostDash.bookings.confirmTitle')}</h3>
            <p className="text-zinc-400 text-sm mb-4">
              {bookingActivityName(confirmCancel, t('hostDash.msg.activity')) + ' · ' + confirmCancel.traveler_name + ' · ' + confirmCancel.start_date}
            </p>
            <div className="bg-[#F9004D]/10 border border-[#F9004D]/30 rounded-lg p-4 mb-6">
              <p className="text-zinc-300 text-sm">{t('hostDash.bookings.confirmBody')}</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={function() { setConfirmCancel(null); }} disabled={cancelling} variant="outline" className="flex-1 border-white/20 text-zinc-300">
                {t('hostDash.bookings.back')}
              </Button>
              <Button onClick={doCancel} disabled={cancelling} className="flex-1 bg-[#F9004D] hover:bg-[#D00040] text-white">
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                {t('hostDash.bookings.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ m }) {
  var { t } = useTranslation();
  var isMe = m.sender === 'candidate';
  var isImage = m.attachment_url && /\.(jpg|jpeg|png|gif|webp)$/i.test(m.attachment_url);
  return (
    <div className={'flex ' + (isMe ? 'justify-end' : 'justify-start')}>
      <div className={'max-w-sm px-3 py-2 rounded-lg text-sm ' + (isMe ? 'bg-[#F9004D] text-white' : 'bg-zinc-700 text-zinc-200')}>
        <p className="font-semibold text-xs opacity-70 mb-1">{isMe ? t('hostDash.msg.you') : t('hostDash.msg.adminName')}</p>
        {m.content && <p>{m.content}</p>}
        {m.attachment_url && (
          isImage
            ? <a href={m.attachment_url} target="_blank" rel="noreferrer"><img src={m.attachment_url} alt="" className="mt-2 max-w-full rounded-lg max-h-48 object-contain" /></a>
            : <a href={m.attachment_url} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-1 underline opacity-90 text-xs"><Eye className="h-3 w-3" /> {t('hostDash.msg.viewDoc')}</a>
        )}
        <p className="text-xs opacity-50 mt-1">{new Date(m.created_at).toLocaleString()}</p>
      </div>
    </div>
  );
}

function ApplicationMessages() {
  var { t } = useTranslation();
  var [msgs, setMsgs] = React.useState([]);
  var [text, setText] = React.useState('');
  var [sending, setSending] = React.useState(false);
  var fileRef = React.useRef(null);

  React.useEffect(function() { loadMsgs(); }, []);

  function loadMsgs() {
    axios.get(API_URL + '/api/guides/me/application/messages', { withCredentials: true })
      .then(function(r) { setMsgs(r.data); })
      .catch(function() {});
  }

  function reply(attachmentUrl) {
    if (!text.trim() && !attachmentUrl) return;
    setSending(true);
    axios.post(API_URL + '/api/guides/me/application/reply', { content: text, attachment_url: attachmentUrl || null }, { withCredentials: true })
      .then(function() { setText(''); loadMsgs(); toast.success(t('hostDash.msg.replySent')); })
      .catch(function() { toast.error(t('hostDash.error')); })
      .finally(function() { setSending(false); });
  }

  function handleFile(e) {
    var file = e.target.files[0];
    if (!file) return;
    setSending(true);
    uploadFile(file)
      .then(function(url) { reply(url); })
      .catch(function() { toast.error(t('hostDash.msg.sendFail')); setSending(false); });
    e.target.value = '';
  }

  if (msgs.length === 0) return null;

  return (
    <Card className="bg-zinc-900/50 border-[#F9004D]/30 p-6 mb-6">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-[#F9004D]" /> {t('hostDash.msg.appTitle')}
      </h3>
      <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
        {msgs.map(function(m) { return <MessageBubble key={m.message_id} m={m} />; })}
      </div>
      <div className="flex gap-2 items-end">
        <textarea value={text} onChange={function(e) { setText(e.target.value); }} placeholder={t('hostDash.msg.replyPlaceholder')} rows={2}
          className="flex-1 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-[#F9004D] outline-none" />
        <div className="flex flex-col gap-1">
          <label title={t('hostDash.msg.attachDoc')} className="cursor-pointer p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-300 flex items-center justify-center">
            <ImageIcon className="h-4 w-4" />
            <input type="file" ref={fileRef} accept="image/*,application/pdf" onChange={handleFile} className="hidden" disabled={sending} />
          </label>
          <Button onClick={function() { reply(null); }} disabled={sending || !text.trim()} className="bg-[#F9004D] hover:bg-[#D00040] text-white p-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function HostMessages() {
  var { t } = useTranslation();
  var [conversations, setConversations] = React.useState([]);
  var [selectedConv, setSelectedConv] = React.useState(null);
  var [messages, setMessages] = React.useState([]);
  var [newMessage, setNewMessage] = React.useState('');
  var [loading, setLoading] = React.useState(true);
  var [sending, setSending] = React.useState(false);

  React.useEffect(function() {
    axios.get(API_URL + '/api/conversations/me', { withCredentials: true })
      .then(function(res) { setConversations(res.data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, []);

  function loadMessages(convId) {
    setSelectedConv(convId);
    axios.get(API_URL + '/api/conversations/' + convId + '/messages', { withCredentials: true })
      .then(function(res) { setMessages(res.data); })
      .catch(function() { toast.error(t('hostDash.msg.loadError')); });
  }

  function sendMsg() {
    if (!newMessage.trim() || !selectedConv) return;
    setSending(true);
    axios.post(API_URL + '/api/conversations/' + selectedConv + '/messages', { content: newMessage }, { withCredentials: true })
      .then(function() {
        setNewMessage('');
        loadMessages(selectedConv);
        setSending(false);
      })
      .catch(function() { toast.error(t('hostDash.error')); setSending(false); });
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;

  if (conversations.length === 0) {
    return (
      <Card className="bg-zinc-900/50 border-white/10 p-8 text-center">
        <MessageSquare className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
        <h3 className="text-xl text-white mb-2">{t('hostDash.msg.none')}</h3>
        <p className="text-zinc-400">{t('hostDash.msg.noneDesc')}</p>
      </Card>
    );
  }

  var convList = [];
  for (var i = 0; i < conversations.length; i++) {
    var c = conversations[i];
    convList.push(
      <div key={c.conversation_id} onClick={function(id) { return function() { loadMessages(id); }; }(c.conversation_id)}
        className={'p-4 cursor-pointer border-b border-white/5 hover:bg-zinc-800/50 ' + (selectedConv === c.conversation_id ? 'bg-zinc-800/50 border-l-2 border-l-[#F9004D]' : '')}
        data-testid={'conv-' + c.conversation_id}>
        <p className="text-white font-bold text-sm">{c.other_user_name || c.traveler_name || t('hostDash.msg.conversation')}</p>
        <p className="text-zinc-500 text-xs">{c.activity_name && typeof c.activity_name === 'object' ? (c.activity_name.fr || c.activity_name.en) : (c.activity_name || t('hostDash.msg.activity'))}</p>
        {c.booking_start_date && <p className="text-[#00F0FF] text-xs mt-0.5">{t('hostDash.msg.bookingDate')} : {c.booking_start_date}</p>}
      </div>
    );
  }

  var msgList = [];
  for (var j = 0; j < messages.length; j++) {
    var m = messages[j];
    var isMe = m.sender_role === 'guide';
    msgList.push(
      <div key={m.message_id || j} className={'flex ' + (isMe ? 'justify-end' : 'justify-start')}>
        <div className={'max-w-[70%] p-3 rounded-xl ' + (isMe ? 'bg-[#F9004D]/20 text-white' : 'bg-zinc-800 text-zinc-300')}>
          <p className="text-sm">{m.content}</p>
          <p className="text-xs text-zinc-500 mt-1">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
    <ApplicationMessages />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ minHeight: '500px' }}>
      <Card className="bg-zinc-900/50 border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="text-white font-bold">{t('hostDash.msg.conversations')}</h3>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: '450px' }}>{convList}</div>
      </Card>
      <Card className="md:col-span-2 bg-zinc-900/50 border-white/10 flex flex-col">
        {selectedConv ? (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '400px' }}>{msgList}</div>
            <div className="p-4 border-t border-white/10 flex gap-2">
              <Input value={newMessage} onChange={function(e) { setNewMessage(e.target.value); }}
                onKeyDown={function(e) { if (e.key === 'Enter') sendMsg(); }}
                placeholder={t('hostDash.msg.placeholder')} className="bg-black/20 border-white/10 text-white flex-1" data-testid="message-input" />
              <Button onClick={sendMsg} disabled={sending} className="bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="send-message-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-500">
            <p>{t('hostDash.msg.selectConv')}</p>
          </div>
        )}
      </Card>
    </div>
    </div>
  );
}

// Profil public du guide : présentation (bio) + niveaux de langue + spécialités
// + zones. Modifiable à tout moment (avant, c'était figé à la candidature).
var PROFILE_LANGS = [
  { key: 'fr', flag: '🇫🇷', label: 'Français' },
  { key: 'en', flag: '🇬🇧', label: 'English' },
  { key: 'jp', flag: '🇯🇵', label: '日本語' }
];
var PROFILE_SPECIALTIES = ['food', 'culture', 'nightlife', 'shopping', 'nature'];

function HostProfile() {
  var { t } = useTranslation();
  var [profile, setProfile] = React.useState(null);
  var [saving, setSaving] = React.useState(false);
  var [uploadingProfilePhoto, setUploadingProfilePhoto] = React.useState(false);
  // { kind: 'avatar'|'cover', file: File|null }  (file null => re-cadrage de l'originale)
  var [photoJob, setPhotoJob] = React.useState(null);

  React.useEffect(function() {
    axios.get(API_URL + '/api/guides/me/profile', { withCredentials: true })
      .then(function(res) {
        var p = res.data || {};
        setProfile({
          bio_fr: (p.bio && p.bio.fr) || '',
          bio_en: (p.bio && p.bio.en) || '',
          bio_jp: (p.bio && p.bio.jp) || '',
          languages_spoken: p.languages_spoken || [],
          language_levels: p.language_levels || {},
          specialties: p.specialties || [],
          areas: p.areas || [],
          profile_photo_url: p.profile_photo_url || '',
          profile_photo_original_url: p.profile_photo_original_url || '',
          profile_photo_crop: p.profile_photo_crop || null,
          cover_photo_url: p.cover_photo_url || '',
          cover_photo_original_url: p.cover_photo_original_url || '',
          cover_photo_crop: p.cover_photo_crop || null
        });
      })
      .catch(function() { toast.error(t('hostDash.error')); });
  }, []);

  function toggleLang(key) {
    var langs = profile.languages_spoken || [];
    var on = langs.indexOf(key) >= 0;
    var levels = Object.assign({}, profile.language_levels);
    if (on) { delete levels[key]; }
    else if (!levels[key]) { levels[key] = 1; }
    setProfile(Object.assign({}, profile, {
      languages_spoken: on ? langs.filter(function(l) { return l !== key; }) : langs.concat([key]),
      language_levels: levels
    }));
  }

  function setLevel(key, n) {
    var levels = Object.assign({}, profile.language_levels);
    levels[key] = n;
    setProfile(Object.assign({}, profile, { language_levels: levels }));
  }

  function toggleSpecialty(spec) {
    var list = profile.specialties || [];
    var on = list.indexOf(spec) >= 0;
    setProfile(Object.assign({}, profile, {
      specialties: on ? list.filter(function(s) { return s !== spec; }) : list.concat([spec])
    }));
  }

  function levelLabel(lvl) {
    if (!lvl) return '—';
    if (lvl <= 2) return t('becomeHost.levels.beginner');
    if (lvl <= 4) return t('becomeHost.levels.intermediate');
    if (lvl <= 5) return t('becomeHost.levels.advanced');
    if (lvl === 6) return t('becomeHost.levels.fluent');
    return t('becomeHost.levels.native');
  }

  // La photo passe d'abord par le recadrage imposé (carré) avant l'envoi.
  function pickPhoto(kind, e) {
    var file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    // Photo iPhone (HEIC/HEIF) : aucun navigateur de bureau ne sait la décoder.
    // On la fait convertir en JPEG par le serveur, puis on cadre le résultat.
    var heic = /\.(heic|heif)$/i.test(file.name || '') || /heic|heif/i.test(file.type || '');
    if (heic) {
      setUploadingProfilePhoto(true);
      uploadFile(file)
        .then(function(url) { setPhotoJob({ kind: kind, file: null, src: url }); })
        .catch(function() { toast.error(t('hostDash.form.uploadFail')); })
        .finally(function() { setUploadingProfilePhoto(false); });
      return;
    }
    setPhotoJob({ kind: kind, file: file });
  }

  // Nouvelle photo : on envoie l'originale EN PLUS du recadrage, pour que le guide
  // puisse revenir modifier son cadrage plus tard sans re-téléverser.
  // Re-cadrage : l'originale est déjà sur le serveur, on n'envoie que le résultat.
  function uploadCropped(croppedFile, crop) {
    if (!photoJob) return;
    var isCover = photoJob.kind === 'cover';
    setUploadingProfilePhoto(true);
    var jobs = [uploadFile(croppedFile)];
    if (photoJob.file) jobs.push(uploadFile(photoJob.file));
    var fields;
    Promise.all(jobs)
      .then(function(urls) {
        // L'originale vient soit d'être envoyée (urls[1]), soit elle est déjà en
        // ligne : HEIC converti par le serveur, ou re-cadrage d'une photo existante.
        var original = urls[1] || photoJob.src;
        if (isCover) {
          fields = { cover_photo_url: urls[0], cover_photo_crop: crop };
          if (original) fields.cover_photo_original_url = original;
        } else {
          fields = { profile_photo_url: urls[0], profile_photo_crop: crop };
          if (original) fields.profile_photo_original_url = original;
        }
        // Enregistrement immédiat : la photo ne doit PAS dépendre du bouton
        // « Enregistrer » situé bien plus bas dans la page — sinon le message
        // de confirmation est mensonger et le cadrage est perdu au rechargement.
        return axios.put(API_URL + '/api/guides/me/profile', fields, { withCredentials: true });
      })
      .then(function() {
        setProfile(Object.assign({}, profile, fields));
        setUploadingProfilePhoto(false);
        setPhotoJob(null);
        toast.success(t('hostDash.profile.photoAdded'));
      })
      .catch(function() { setUploadingProfilePhoto(false); toast.error(t('hostDash.form.uploadFail')); });
  }

  function removeCover() {
    setUploadingProfilePhoto(true);
    axios.put(API_URL + '/api/guides/me/profile', { cover_photo_url: '' }, { withCredentials: true })
      .then(function() {
        setProfile(Object.assign({}, profile, { cover_photo_url: '' }));
        toast.success(t('hostDash.profile.coverRemoved'));
      })
      .catch(function() { toast.error(t('hostDash.error')); })
      .finally(function() { setUploadingProfilePhoto(false); });
  }

  function save() {
    setSaving(true);
    axios.put(API_URL + '/api/guides/me/profile', {
      bio_fr: profile.bio_fr,
      bio_en: profile.bio_en,
      bio_jp: profile.bio_jp,
      languages_spoken: profile.languages_spoken,
      language_levels: profile.language_levels,
      specialties: profile.specialties,
      areas: profile.areas,
      profile_photo_url: profile.profile_photo_url,
      profile_photo_original_url: profile.profile_photo_original_url,
      profile_photo_crop: profile.profile_photo_crop,
      cover_photo_url: profile.cover_photo_url,
      cover_photo_original_url: profile.cover_photo_original_url,
      cover_photo_crop: profile.cover_photo_crop
    }, { withCredentials: true })
      .then(function() { toast.success(t('hostDash.profile.saved')); setSaving(false); })
      .catch(function(err) { toast.error(apiErr(err, t('hostDash.error'))); setSaving(false); });
  }

  if (!profile) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-6" data-testid="host-profile-tab">
      <div className="space-y-6">
        {/* Photo de profil (avatar) */}
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{t('hostDash.profile.photoTitle')}</h3>
          <p className="text-zinc-400 text-sm mb-4">{t('hostDash.profile.photoHint')}</p>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-24 h-24 rounded-full overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center flex-shrink-0">
              {profile.profile_photo_url ? (
                <img src={profile.profile_photo_url} alt="profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
              ) : (
                <ImageIcon className="h-8 w-8 text-zinc-600" />
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-zinc-200 cursor-pointer hover:border-[#F9004D]/50" data-testid="profile-photo-label">
              {uploadingProfilePhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              <span>{uploadingProfilePhoto ? '...' : t('hostDash.profile.changePhoto')}</span>
              <input type="file" accept="image/*" onChange={function(e) { pickPhoto('avatar', e); }} className="hidden" disabled={uploadingProfilePhoto} data-testid="profile-photo-input" />
            </label>
            {profile.profile_photo_original_url && (
              <button
                type="button"
                onClick={function() { setPhotoJob({ kind: 'avatar', file: null }); }}
                disabled={uploadingProfilePhoto}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-zinc-200 hover:border-[#F9004D]/50 disabled:opacity-50"
                data-testid="recrop-btn"
              >
                <Crop className="h-4 w-4" />
                <span>{t('hostDash.profile.recrop')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Photo de couverture : independante de l'avatar (paysage, vue de Tokyo...) */}
        <div className="pt-6 border-t border-white/10">
          <h3 className="text-xl font-bold text-white mb-1">{t('hostDash.profile.coverTitle')}</h3>
          <p className="text-zinc-400 text-sm mb-4">{t('hostDash.profile.coverHint')}</p>
          <div className="w-full max-w-lg aspect-video rounded-xl overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center mb-4">
            {profile.cover_photo_url ? (
              <img src={profile.cover_photo_url} alt="couverture" className="w-full h-full object-cover" data-testid="cover-photo-preview" />
            ) : (
              <ImageIcon className="h-10 w-10 text-zinc-600" />
            )}
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-zinc-200 cursor-pointer hover:border-[#F9004D]/50" data-testid="cover-photo-label">
              {uploadingProfilePhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              <span>{profile.cover_photo_url ? t('hostDash.profile.changeCover') : t('hostDash.profile.addCover')}</span>
              <input type="file" accept="image/*" onChange={function(e) { pickPhoto('cover', e); }} className="hidden" disabled={uploadingProfilePhoto} data-testid="cover-photo-input" />
            </label>
            {profile.cover_photo_original_url && (
              <button
                type="button"
                onClick={function() { setPhotoJob({ kind: 'cover', file: null }); }}
                disabled={uploadingProfilePhoto}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-zinc-200 hover:border-[#F9004D]/50 disabled:opacity-50"
                data-testid="recrop-cover-btn"
              >
                <Crop className="h-4 w-4" />
                <span>{t('hostDash.profile.recrop')}</span>
              </button>
            )}
            {profile.cover_photo_url && (
              <button
                type="button"
                onClick={removeCover}
                disabled={uploadingProfilePhoto}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                <span>{t('hostDash.profile.removeCover')}</span>
              </button>
            )}
          </div>
        </div>

        {photoJob && (
          <PhotoCropper
            file={photoJob.file}
            src={photoJob.file ? undefined : (photoJob.src || (photoJob.kind === 'cover' ? profile.cover_photo_original_url : profile.profile_photo_original_url))}
            initialCrop={(photoJob.file || photoJob.src) ? undefined : (photoJob.kind === 'cover' ? profile.cover_photo_crop : profile.profile_photo_crop)}
            aspect={photoJob.kind === 'cover' ? 16 / 9 : 1}
            outWidth={photoJob.kind === 'cover' ? 1600 : 800}
            round={photoJob.kind !== 'cover'}
            title={photoJob.kind === 'cover' ? t('hostDash.profile.cropCover') : t('hostDash.profile.cropAvatar')}
            uploading={uploadingProfilePhoto}
            onCancel={function() { setPhotoJob(null); }}
            onCropped={uploadCropped}
          />
        )}

        {/* Présentation */}
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{t('hostDash.profile.bioTitle')}</h3>
          <p className="text-zinc-400 text-sm mb-4">{t('hostDash.profile.bioHint')}</p>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300 mb-1 block">{t('becomeHost.bioFr')}</Label>
              <textarea value={profile.bio_fr} onChange={function(e) { setProfile(Object.assign({}, profile, { bio_fr: e.target.value })); }}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white min-h-[110px]"
                placeholder={t('becomeHost.bioFrPlaceholder')} data-testid="profile-bio-fr" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 mb-1 block">{t('becomeHost.bioEn')}</Label>
                <textarea value={profile.bio_en} onChange={function(e) { setProfile(Object.assign({}, profile, { bio_en: e.target.value })); }}
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white min-h-[90px]"
                  placeholder={t('becomeHost.bioEnPlaceholder')} />
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 block">{t('becomeHost.bioJp')}</Label>
                <textarea value={profile.bio_jp} onChange={function(e) { setProfile(Object.assign({}, profile, { bio_jp: e.target.value })); }}
                  className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white min-h-[90px]"
                  placeholder={t('becomeHost.bioJpPlaceholder')} />
              </div>
            </div>
          </div>
        </div>

        {/* Langues et niveaux */}
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{t('becomeHost.languagesSpoken')}</h3>
          <p className="text-zinc-400 text-sm mb-4">{t('becomeHost.languagesHint')}</p>
          <div className="space-y-3">
            {PROFILE_LANGS.map(function(l) {
              var checked = (profile.languages_spoken || []).indexOf(l.key) >= 0;
              var lvl = (profile.language_levels || {})[l.key] || 0;
              return (
                <div key={l.key} className={'rounded-xl border transition-all p-4 ' + (checked ? 'border-[#F9004D]/50 bg-[#F9004D]/5' : 'border-white/10 bg-black/10')}>
                  <button type="button" onClick={function() { toggleLang(l.key); }}
                    className="flex items-center gap-3 text-white font-semibold" data-testid={'profile-lang-' + l.key}>
                    <span className={'w-4 h-4 rounded border flex items-center justify-center ' + (checked ? 'bg-[#F9004D] border-[#F9004D]' : 'border-white/20')}>
                      {checked && <Check className="h-3 w-3 text-white" />}
                    </span>
                    {l.flag} {l.label}
                  </button>
                  {checked && (
                    <div className="flex items-center gap-2 pl-7 mt-3 flex-wrap">
                      <span className="text-zinc-500 text-xs">{t('becomeHost.levelLabel')}</span>
                      <div className="flex gap-1.5">
                        {[1, 2, 3, 4, 5, 6, 7].map(function(n) {
                          return (
                            <button key={n} type="button" onClick={function() { setLevel(l.key, n); }}
                              className={'w-7 h-7 rounded-full border-2 text-xs font-bold transition-colors ' +
                                (lvl >= n ? 'bg-[#F9004D] border-[#F9004D] text-white' : 'bg-transparent border-white/20 text-zinc-500 hover:border-white/50')}>
                              {n}
                            </button>
                          );
                        })}
                      </div>
                      <span className="text-zinc-400 text-xs ml-1">{levelLabel(lvl)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Spécialités */}
        <div>
          <h3 className="text-xl font-bold text-white mb-3">{t('becomeHost.specialties')}</h3>
          <div className="flex flex-wrap gap-2">
            {PROFILE_SPECIALTIES.map(function(spec) {
              var on = (profile.specialties || []).indexOf(spec) >= 0;
              return (
                <button key={spec} type="button" onClick={function() { toggleSpecialty(spec); }}
                  className={'px-4 py-2 rounded-full border text-sm transition-colors ' +
                    (on ? 'bg-[#F9004D] border-[#F9004D] text-white' : 'border-white/20 text-zinc-300 hover:border-white/40')}>
                  {t('becomeHost.specialtyOptions.' + spec)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Zones */}
        <div>
          <h3 className="text-xl font-bold text-white mb-1">{t('becomeHost.areas')}</h3>
          <p className="text-zinc-400 text-sm mb-3">{t('becomeHost.areasHint')}</p>
          <Input value={(profile.areas || []).join(', ')}
            onChange={function(e) { setProfile(Object.assign({}, profile, { areas: e.target.value.split(',').map(function(s) { return s.trim(); }).filter(Boolean) })); }}
            placeholder={t('becomeHost.areasPlaceholder')}
            className="bg-black/20 border-white/10 text-white" data-testid="profile-areas" />
        </div>

        <Button onClick={save} disabled={saving} className="bg-[#F9004D] hover:bg-[#D00040] text-white px-8" data-testid="save-profile-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
          {t('hostDash.form.save')}
        </Button>
      </div>
    </Card>
  );
}

// Devise dans laquelle le guide fixe ses tarifs ET reçoit ses paiements.
// Il touche exactement ce montant, quelle que soit la devise d'affichage du voyageur.
function HostPayoutCurrency() {
  var { t } = useTranslation();
  var [currency, setCurrency] = React.useState(null);
  var [saving, setSaving] = React.useState(false);

  React.useEffect(function() {
    axios.get(API_URL + '/api/guides/me/profile', { withCredentials: true })
      .then(function(res) { setCurrency((res.data && res.data.payout_currency) || 'JPY'); })
      .catch(function() { setCurrency('JPY'); });
  }, []);

  function choose(code) {
    if (code === currency || saving) return;
    setSaving(true);
    axios.put(API_URL + '/api/guides/me/profile', { payout_currency: code }, { withCredentials: true })
      .then(function() {
        setCurrency(code);
        toast.success(t('hostDash.currency.updated', { code: code }));
        setSaving(false);
      })
      .catch(function() { toast.error(t('hostDash.currency.error')); setSaving(false); });
  }

  if (!currency) return null;

  var options = [
    { code: 'JPY', label: '¥ Yen (JPY)' },
    { code: 'EUR', label: '€ Euro (EUR)' },
    { code: 'USD', label: '$ Dollar (USD)' }
  ];

  return (
    <Card className="bg-zinc-900/80 p-6 rounded-xl border border-white/5 mb-6" data-testid="host-payout-currency">
      <h3 className="text-white font-bold mb-1">Devise de vos tarifs et paiements</h3>
      <p className="text-zinc-400 text-sm mb-4">
        Vos tarifs sont exprimés dans cette devise et vous recevez exactement ce montant.
        Les voyageurs peuvent afficher les prix dans leur propre devise, sans que cela change ce que vous percevez.
      </p>
      <div className="flex flex-wrap gap-3">
        {options.map(function(o) {
          var active = currency === o.code;
          return (
            <button key={o.code} type="button" onClick={function() { choose(o.code); }} disabled={saving}
              className={'px-4 py-2 rounded-full border text-sm transition-colors disabled:opacity-50 ' +
                (active ? 'bg-[#F9004D] border-[#F9004D] text-white' : 'border-white/20 text-zinc-300 hover:border-white/40')}>
              {o.label}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function HostRevenue() {
  var { t } = useTranslation();
  var [data, setData] = React.useState(null);
  var cfg = useConfig();

  React.useEffect(function() {
    axios.get(API_URL + '/api/guides/me/revenue', { withCredentials: true })
      .then(function(res) { setData(res.data); })
      .catch(function() { toast.error(t('hostDash.revenue.loadError')); });
  }, []);

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>;

  var monthly = (data.monthly_earnings || []).map(function(m) {
    return { month: m.month.slice(5), Gains: m.earnings, Reservations: m.bookings };
  });

  var cards = [
    { color: 'text-green-400', value: formatYen(data.host_earnings), label: t('hostDash.revenue.gains', { percent: cfg.guide_fee_percent }), icon: Wallet },
    { color: 'text-white', value: formatYen(data.gross_revenue), label: t('hostDash.revenue.gross'), icon: TrendingUp },
    { color: 'text-[#00F0FF]', value: data.confirmed_bookings, label: t('hostDash.revenue.confirmed'), icon: Calendar },
    { color: 'text-red-400', value: data.cancelled_bookings, label: t('hostDash.revenue.cancelled'), icon: X }
  ];
  var cardEls = [];
  for (var i = 0; i < cards.length; i++) {
    var c = cards[i];
    var Ic = c.icon;
    cardEls.push(
      <Card key={i} className="bg-zinc-900/80 p-6 rounded-xl border border-white/5" data-testid={'revenue-card-' + i}>
        <div className="flex items-center justify-between mb-2">
          <Ic className={'h-7 w-7 ' + c.color} />
          <div className={'text-2xl font-bold ' + c.color} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{c.value}</div>
        </div>
        <div className="text-sm text-zinc-400">{c.label}</div>
      </Card>
    );
  }

  return (
    <div className="space-y-8" data-testid="host-revenue">
      <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('hostDash.revenue.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">{cardEls}</div>
      <Card className="bg-zinc-900/50 border-white/10 p-6">
        <h3 className="text-lg font-bold text-white mb-4">{t('hostDash.revenue.monthly')}</h3>
        <ResponsiveContainer width="100%" height={280} data-testid="chart-monthly-earnings">
          <BarChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="month" stroke="#a1a1aa" fontSize={12} />
            <YAxis stroke="#a1a1aa" fontSize={12} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, color: '#fff' }}
              formatter={function(v, n) { return n === 'Gains' ? formatYen(v) : v; }} />
            <Bar dataKey="Gains" fill="#22c55e" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <p className="text-zinc-500 text-sm">{t('hostDash.revenue.commissionNote', { platformFee: cfg.platform_fee_percent, hostShare: cfg.guide_fee_percent })}</p>

      {/* Stripe Connect Setup */}
      <StripeConnectCard />
    </div>
  );
}

// Pays où un guide peut ouvrir un compte de versement Stripe (doit rester aligné
// avec ALLOWED_HOST_COUNTRIES côté backend). Le pays = résidence réelle du guide.
var HOST_COUNTRIES = [
  { code: 'JP', label: 'Japon' },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Allemagne' },
  { code: 'ES', label: 'Espagne' },
  { code: 'IT', label: 'Italie' },
  { code: 'BE', label: 'Belgique' },
  { code: 'NL', label: 'Pays-Bas' },
  { code: 'PT', label: 'Portugal' },
  { code: 'IE', label: 'Irlande' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'AT', label: 'Autriche' },
  { code: 'CH', label: 'Suisse' },
  { code: 'GB', label: 'Royaume-Uni' },
  { code: 'US', label: 'États-Unis' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australie' },
  { code: 'NZ', label: 'Nouvelle-Zélande' },
  { code: 'SG', label: 'Singapour' },
  { code: 'HK', label: 'Hong Kong' }
];

// Sélecteur du pays de résidence du guide, présenté avant de connecter sa
// banque : le pays fixe les champs et l'IBAN demandés par Stripe.
function HostCountrySelect(props) {
  var { t } = useTranslation();
  return (
    <div className="mb-4">
      <label className="block text-zinc-300 text-sm mb-1">{t('hostDash.stripe.countryLabel')}</label>
      <select value={props.value} onChange={function(e) { props.onChange(e.target.value); }} disabled={props.disabled}
        data-testid="host-country-select"
        className="w-full sm:w-72 bg-zinc-800 border border-white/15 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#F9004D] disabled:opacity-50">
        {HOST_COUNTRIES.map(function(c) {
          return <option key={c.code} value={c.code}>{c.label}</option>;
        })}
      </select>
      <p className="text-zinc-500 text-xs mt-1">{t('hostDash.stripe.countryHint')}</p>
    </div>
  );
}

function StripeConnectCard() {
  var { t } = useTranslation();
  var cfg = useConfig();
  var [status, setStatus] = React.useState(null);
  var [loading, setLoading] = React.useState(true);
  var [onboarding, setOnboarding] = React.useState(false);
  var [country, setCountry] = React.useState('JP');

  React.useEffect(function() {
    axios.get(API_URL + '/api/guides/me/stripe/status', { withCredentials: true })
      .then(function(r) { setStatus(r.data); if (r.data && r.data.country) setCountry(r.data.country); })
      .catch(function() {})
      .finally(function() { setLoading(false); });

    // Handle return from Stripe onboarding
    var params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'success') toast.success(t('hostDash.stripe.connected'));
    if (params.get('stripe') === 'refresh') toast.info(t('hostDash.stripe.interrupted'));
  }, []);

  function startOnboarding() {
    setOnboarding(true);
    axios.post(API_URL + '/api/guides/me/stripe/onboard', { country: country }, { withCredentials: true })
      .then(function(r) {
        if (r.data.onboarding_url) {
          window.location.href = r.data.onboarding_url;
        } else {
          toast.info(r.data.message || t('hostDash.stripe.testModeToast'));
          setOnboarding(false);
        }
      })
      .catch(function(e) { toast.error(e.response?.data?.detail || t('hostDash.error')); setOnboarding(false); });
  }

  if (loading) return null;

  var connected = status && status.connected;
  var testMode = status && status.test_mode;

  return (
    <Card className="bg-zinc-900/50 border-white/10 p-6">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl" style={{ background: connected ? '#10B98120' : '#F59E0B20' }}>
          <Banknote className="h-6 w-6" style={{ color: connected ? '#10B981' : '#F59E0B' }} />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-lg mb-1">{t('hostDash.stripe.bankTitle')}</h3>
          {testMode ? (
            <p className="text-zinc-400 text-sm">{t('hostDash.stripe.testModeActive')}</p>
          ) : connected ? (
            <div>
              <p className="text-[#10B981] text-sm font-semibold mb-1">{t('hostDash.stripe.connectedOk')}</p>
              <p className="text-zinc-400 text-sm">{t('hostDash.stripe.payoutInfo', { percent: cfg.guide_fee_percent, days: cfg.payout_hold_days })}</p>
            </div>
          ) : (
            <div>
              <p className="text-[#F59E0B] text-sm font-semibold mb-2">{t('hostDash.stripe.notConfigured')}</p>
              <p className="text-zinc-400 text-sm mb-4">{t('hostDash.stripe.notConfiguredDesc', { days: cfg.payout_hold_days })}</p>
              <HostCountrySelect value={country} onChange={setCountry} disabled={onboarding} />
              <Button onClick={startOnboarding} disabled={onboarding} className="bg-[#F9004D] hover:bg-[#D00040] text-white">
                {onboarding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Banknote className="h-4 w-4 mr-2" />}
                {t('hostDash.stripe.connectBtn')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// Bandeau persistant : tant que le guide n'a pas connecté sa banque (Stripe),
// on l'invite à le faire sur tout le dashboard. Masqué une fois connecté (ou en
// mode test sans Stripe).
function HostBankBanner() {
  var { t } = useTranslation();
  var [status, setStatus] = React.useState(null);
  var [onboarding, setOnboarding] = React.useState(false);
  var [country, setCountry] = React.useState('JP');

  React.useEffect(function() {
    axios.get(API_URL + '/api/guides/me/stripe/status', { withCredentials: true })
      .then(function(r) { setStatus(r.data); if (r.data && r.data.country) setCountry(r.data.country); })
      .catch(function() {});
  }, []);

  function startOnboarding() {
    setOnboarding(true);
    axios.post(API_URL + '/api/guides/me/stripe/onboard', { country: country }, { withCredentials: true })
      .then(function(r) {
        if (r.data.onboarding_url) {
          window.location.href = r.data.onboarding_url;
        } else {
          toast.info(r.data.message || t('hostDash.stripe.testModeToast'));
          setOnboarding(false);
        }
      })
      .catch(function(e) { toast.error(e.response?.data?.detail || t('hostDash.error')); setOnboarding(false); });
  }

  if (!status || status.connected || status.test_mode) return null;

  return (
    <div className="mb-6 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4" data-testid="host-bank-banner">
      <div className="flex items-start gap-3 mb-3">
        <Banknote className="h-6 w-6 text-[#F59E0B] shrink-0 mt-0.5" />
        <div>
          <p className="text-[#F59E0B] font-semibold">{t('hostDash.stripe.bankTitle')}</p>
          <p className="text-zinc-300 text-sm">{t('hostDash.stripe.bannerText')}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1"><HostCountrySelect value={country} onChange={setCountry} disabled={onboarding} /></div>
        <Button onClick={startOnboarding} disabled={onboarding} className="bg-[#F9004D] hover:bg-[#D00040] text-white shrink-0 mb-1">
          {onboarding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Banknote className="h-4 w-4 mr-2" />}
          {t('hostDash.stripe.connectBtn')}
        </Button>
      </div>
    </div>
  );
}

// Propose d'activer les notifications push (nouvelles demandes de réservation).
// Sur iOS, la permission ne peut être demandée QUE depuis l'app ajoutée à
// l'écran d'accueil (limitation Safari) — on affiche alors l'instruction
// d'installation plutôt qu'un bouton qui échouerait silencieusement.
function HostPushBanner() {
  var { t } = useTranslation();
  var [visible, setVisible] = React.useState(false);
  var [enabling, setEnabling] = React.useState(false);
  var needsInstall = isIos() && !isStandalone();

  React.useEffect(function() {
    if (!isPushSupported()) return;
    if (window.Notification && Notification.permission === 'denied') return;
    getPushSubscription().then(function(sub) { if (!sub) setVisible(true); });
  }, []);

  function enable() {
    setEnabling(true);
    subscribeToPush()
      .then(function() { toast.success(t('hostDash.push.enabled')); setVisible(false); })
      .catch(function() { toast.error(t('hostDash.push.error')); })
      .finally(function() { setEnabling(false); });
  }

  if (!visible) return null;

  return (
    <div className="mb-6 rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4" data-testid="host-push-banner">
      <div className="flex items-start gap-3">
        <Bell className="h-6 w-6 text-[#00F0FF] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-[#00F0FF] font-semibold">{t('hostDash.push.title')}</p>
          <p className="text-zinc-300 text-sm">{needsInstall ? t('hostDash.push.iosInstallText') : t('hostDash.push.bannerText')}</p>
        </div>
        {!needsInstall && (
          <Button onClick={enable} disabled={enabling} className="bg-[#00F0FF] hover:bg-[#00c8d6] text-black shrink-0">
            {enabling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
            {t('hostDash.push.enableBtn')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function GuideDashboard() {
  var { t } = useTranslation();
  var navigate = useNavigate();
  var [activeTab, setActiveTab] = React.useState('activities');
  var [activities, setActivities] = React.useState([]);
  var [loading, setLoading] = React.useState(true);
  var [showForm, setShowForm] = React.useState(false);
  var [editingActivity, setEditingActivity] = React.useState(null);
  var [profileStatus, setProfileStatus] = React.useState(null);

  React.useEffect(function() {
    fetchActivities();
    axios.get(API_URL + '/api/guides/me/profile', { withCredentials: true })
      .then(function(res) { setProfileStatus(res.data.status); })
      .catch(function() {});
  }, []);

  function fetchActivities() {
    setLoading(true);
    axios.get(API_URL + '/api/guides/me/activities', { withCredentials: true })
      .then(function(res) { setActivities(res.data); setLoading(false); })
      .catch(function() { toast.error(t('hostDash.main.loadError')); setLoading(false); });
  }

  function handleCreate(form, done) {
    // L'API attend des champs plats (name_fr, description_fr...).
    var payload = {
      location: form.location,
      meeting_point: form.meeting_point,
      meeting_point_url: form.meeting_point_url,
      start_time: form.start_time,
      duration_hours: form.duration_hours,
      name_fr: form.name_fr, name_en: form.name_en, name_jp: form.name_jp,
      description_fr: form.description_fr, description_en: form.description_en, description_jp: form.description_jp,
      adult_rate: form.adult_rate,
      child_rate: form.child_rate,
      children_allowed: form.children_allowed,
      max_participants: form.max_participants,
      min_notice_days: parseInt(form.min_notice_days, 10) || 0,
      group_discount_enabled: form.group_discount_enabled,
      group_discounts: form.group_discounts,
      included: form.included || [],
      not_included: form.not_included || [],
      activity_type: form.activity_type || 'journee',
      destinations: form.destinations || [],
      duration_days: form.duration_days || null,
      itinerary: form.itinerary || [],
      pricing_mode: form.pricing_mode || 'par_jour',
      min_participants: form.min_participants || 1,
      package_prices: form.package_prices || {}
    };
    var finishCreate = function() {
      setShowForm(false);
      setEditingActivity(null);
      fetchActivities();
      done();
    };
    axios.post(API_URL + '/api/guides/me/activities', payload, { withCredentials: true })
      .then(function(res) {
        var newId = res.data.activity_id;
        if (!newId) { toast.success(t('hostDash.main.created')); finishCreate(); return; }
        if (form.photos && form.photos.length) {
          axios.put(API_URL + '/api/guides/me/activities/' + newId + '/photos', { photos: form.photos }, { withCredentials: true }).catch(function() {});
        }
        // Règle la disponibilité, PUIS soumet automatiquement (plus de clic "Soumettre").
        axios.put(API_URL + '/api/guides/me/activities/' + newId + '/availability', {
          days_of_week: form.days_of_week,
          time_slots: slotFromTime(form.start_time),
          start_date: form.start_date || null,
          end_date: form.end_date || null
        }, { withCredentials: true })
          .then(function() {
            return axios.post(API_URL + '/api/guides/me/activities/' + newId + '/submit', {}, { withCredentials: true });
          })
          .then(function() { toast.success(t('hostDash.main.createdSubmitted')); finishCreate(); })
          .catch(function(err) {
            // Soumission impossible (infos manquantes) : l'activité reste en brouillon.
            toast.info(apiErr(err, t('hostDash.main.created')));
            finishCreate();
          });
      })
      .catch(function(err) {
        toast.error(apiErr(err, t('hostDash.error')));
        done();
      });
  }

  function handleUpdate(form, done) {
    // L'API attend des champs plats (name_fr, description_fr...).
    var payload = {
      meeting_point: form.meeting_point,
      meeting_point_url: form.meeting_point_url,
      start_time: form.start_time,
      duration_hours: form.duration_hours,
      name_fr: form.name_fr, name_en: form.name_en, name_jp: form.name_jp,
      description_fr: form.description_fr, description_en: form.description_en, description_jp: form.description_jp,
      adult_rate: form.adult_rate,
      child_rate: form.child_rate,
      children_allowed: form.children_allowed,
      max_participants: form.max_participants,
      min_notice_days: parseInt(form.min_notice_days, 10) || 0,
      group_discount_enabled: form.group_discount_enabled,
      group_discounts: form.group_discounts,
      included: form.included || [],
      not_included: form.not_included || [],
      activity_type: form.activity_type || 'journee',
      destinations: form.destinations || [],
      duration_days: form.duration_days || null,
      itinerary: form.itinerary || [],
      pricing_mode: form.pricing_mode || 'par_jour',
      min_participants: form.min_participants || 1,
      package_prices: form.package_prices || {}
    };
    axios.put(API_URL + '/api/guides/me/activities/' + editingActivity.activity_id, payload, { withCredentials: true })
      .then(function(res) {
        var needsApproval = !!(res && res.data && res.data.needs_approval);
        if (form.photos) {
          axios.put(API_URL + '/api/guides/me/activities/' + editingActivity.activity_id + '/photos', { photos: form.photos }, { withCredentials: true })
            .then(function(pr) { if (pr && pr.data && pr.data.needs_approval) { toast.info(t('hostDash.main.photoSubmitted')); } })
            .catch(function() {});
        }
        toast.success(needsApproval ? t('hostDash.main.modSubmitted') : t('hostDash.main.updated'));
        setShowForm(false);
        setEditingActivity(null);
        fetchActivities();
        done();

        // Also update availability
        var availPayload = {
          days_of_week: form.days_of_week,
          time_slots: slotFromTime(form.start_time),
          start_date: form.start_date || null,
          end_date: form.end_date || null
        };
        axios.put(API_URL + '/api/guides/me/activities/' + editingActivity.activity_id + '/availability', availPayload, { withCredentials: true }).catch(function() {});
      })
      .catch(function(err) {
        toast.error(apiErr(err, t('hostDash.error')));
        done();
      });
  }

  function handleSubmit(activityId) {
    axios.post(API_URL + '/api/guides/me/activities/' + activityId + '/submit', {}, { withCredentials: true })
      .then(function() { toast.success(t('hostDash.main.submitted')); fetchActivities(); })
      .catch(function(err) { toast.error(apiErr(err, t('hostDash.error'))); });
  }

  function handleToggle(activityId) {
    axios.put(API_URL + '/api/guides/me/activities/' + activityId + '/toggle', {}, { withCredentials: true })
      .then(function() { toast.success(t('hostDash.main.statusUpdated')); fetchActivities(); })
      .catch(function(err) { toast.error(apiErr(err, t('hostDash.error'))); });
  }

  function handleDuplicate(activityId) {
    axios.post(API_URL + '/api/guides/me/activities/' + activityId + '/duplicate', {}, { withCredentials: true })
      .then(function(res) {
        toast.success(t('hostDash.main.duplicated'));
        fetchActivities();
        // Ouvre directement le duplicata en édition.
        axios.get(API_URL + '/api/guides/me/activities', { withCredentials: true }).then(function(r) {
          for (var i = 0; i < r.data.length; i++) {
            if (r.data[i].activity_id === res.data.activity_id) { openEdit(r.data[i]); break; }
          }
        }).catch(function() {});
      })
      .catch(function(err) { toast.error(apiErr(err, t('hostDash.error'))); });
  }

  function openEdit(activity) {
    setEditingActivity(activity);
    setShowForm(true);
  }

  function openCreate() {
    setEditingActivity(null);
    setShowForm(true);
  }

  var activityCards = [];
  if (!loading) {
    for (var i = 0; i < activities.length; i++) {
      activityCards.push(
        <ActivityCard key={activities[i].activity_id} activity={activities[i]}
          onEdit={openEdit} onToggle={handleToggle} onSubmit={handleSubmit} onDuplicate={handleDuplicate} />
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="guide-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {profileStatus === 'pending' && (
          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-4 flex items-start gap-3" data-testid="candidature-pending-banner">
            <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-300 font-semibold">{t('hostDash.candidature.pendingTitle')}</p>
              <p className="text-zinc-400 text-sm">{t('hostDash.candidature.pendingDesc')}</p>
            </div>
          </div>
        )}
        {profileStatus === 'rejected' && (
          <div className="mb-6 bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex items-start gap-3" data-testid="candidature-rejected-banner">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-semibold">{t('hostDash.candidature.rejectedTitle')}</p>
              <p className="text-zinc-400 text-sm">{t('hostDash.candidature.rejectedDesc')}</p>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            {t('hostDash.espaceHote')}
          </h1>
        </div>

        <HostBankBanner />
        <HostPushBanner />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-zinc-900 border border-white/10 p-1">
            <TabsTrigger value="activities" className="data-[state=active]:bg-[#F9004D]" data-testid="tab-activities">
              <MapPin className="h-4 w-4 mr-2" />
              {t('hostDash.tabs.activities')}
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#F9004D]" data-testid="tab-bookings">
              <Calendar className="h-4 w-4 mr-2" />
              {t('hostDash.tabs.bookings')}
            </TabsTrigger>
            <TabsTrigger value="availability" className="data-[state=active]:bg-[#F9004D]" data-testid="tab-availability">
              <BanIcon className="h-4 w-4 mr-2" />
              {t('hostDash.tabs.availability')}
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-[#F9004D]" data-testid="tab-messages">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('hostDash.tabs.messages')}
            </TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-[#F9004D]" data-testid="tab-revenue">
              <Wallet className="h-4 w-4 mr-2" />
              {t('hostDash.tabs.revenue')}
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#F9004D]" data-testid="tab-profile">
              <User className="h-4 w-4 mr-2" />
              {t('hostDash.tabs.profile')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="activities">
            {showForm ? (
              <ActivityForm
                activity={editingActivity}
                onSave={editingActivity ? handleUpdate : handleCreate}
                onCancel={function() { setShowForm(false); setEditingActivity(null); }}
              />
            ) : (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <p className="text-zinc-400">{t('hostDash.main.activitiesCount', { count: activities.length })}</p>
                  <Button onClick={openCreate} className="bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="create-activity-btn">
                    <Plus className="h-4 w-4 mr-2" /> {t('hostDash.main.newActivity')}
                  </Button>
                </div>
                {loading ? (
                  <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>
                ) : activityCards.length === 0 ? (
                  <Card className="bg-zinc-900/50 border-white/10 p-12 text-center">
                    <MapPin className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-xl text-white mb-2">{t('hostDash.main.noActivity')}</h3>
                    <p className="text-zinc-400 mb-6">{t('hostDash.main.noActivityDesc')}</p>
                    <Button onClick={openCreate} className="bg-[#F9004D] hover:bg-[#D00040] text-white">
                      <Plus className="h-4 w-4 mr-2" /> {t('hostDash.main.createActivity')}
                    </Button>
                  </Card>
                ) : (
                  <div className="space-y-4">{activityCards}</div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings">
            <HostBookings />
          </TabsContent>

          <TabsContent value="availability">
            <AvailabilityCalendar />
          </TabsContent>

          <TabsContent value="messages">
            <HostMessages />
          </TabsContent>

          <TabsContent value="revenue">
            <HostPayoutCurrency />
            <HostRevenue />
          </TabsContent>

          <TabsContent value="profile">
            <HostProfile />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
